/**
 * Music Video Maker — engine (Tier-1).
 *
 * Pipeline (root-cause architecture from project_music_video_maker_research):
 *   1. Song  -> AudioDecoderRobust.robust (native->WebCodecs->FFmpeg->repair)
 *              -> __detectBPM (octave-corrected, shipped primitive).
 *   2. Clips -> probe duration + poster via <video>+canvas (NOT a fragile
 *              per-clip FFmpeg-WASM thumbnail exec — that class is 29% of
 *              /video-merge errors in prod telemetry).
 *   3. Auto-fill: tile the song with beat-length segments, cycling clips.
 *   4. Render EACH segment as its own CFR clip (-fps_mode cfr) one clip
 *      mounted at a time -> bounds frame memory (sab:0 single-thread, 17%
 *      of users report mem:0). VFR phone clips normalized via fps=30 +
 *      -fps_mode cfr (closes the 1.48s A/V-drift root proven in de-risk).
 *   5. Concat segments (-c copy) + mux the song (volume + true-peak limiter),
 *      song trimmed to the montage length -> always MP4 (H.264).
 *
 * Tier-2 (next): per-clip keep-audio + sidechain ducking, beat-snap to real
 * onset times, zoom-pulse-on-beat effect.
 */
(function () {
    'use strict';

    var CFG = window.__mvmCfg || { maxClips: 12, t: {} };
    var T = CFG.t || {};

    /* Перевод уезжает в АТРИБУТ склейкой строк, поэтому кавычку в нём надо
       снимать: французское «l'aperçu» и любые кавычки в чужой локали иначе
       закрывают атрибут раньше времени и ломают разметку кнопки. */
    function esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    var SLUG = 'music-video-maker';
    var MAX_SEGMENTS = 48;

    /* Adaptive clip cap. The render cost is bound by the number of SEGMENTS
       (≤ MAX_SEGMENTS), which tile the SONG — NOT by the clip count: 3 clips or
       30 encode the same output. Extra clips cost only memory (each is demuxed
       and its compressed samples cached for the montage) + a little demux time.
       So the ceiling is a memory budget, and low-memory devices keep the
       floor (CFG.maxClips = 12 — never regress). navigator.deviceMemory is
       spec-capped at 8, so this is 12 (≤4 GB, incl. the reported 4 GB Android),
       18 (6 GB), 24 (≥8 GB), never above MAX_SEGMENTS (more clips than segments
       = some never appear). WebCodecs handles the higher counts in bounded
       memory; the FFmpeg fallback mounts one clip at a time, so both paths are
       clip-count-safe — the guard is purely the demux-cache heap. */
    function computeMaxClips() {
        var floor = CFG.maxClips || 12;
        var mem = navigator.deviceMemory || 4;   // spec-capped at 8; undefined→4
        var cap = floor;
        if (mem >= 8) cap = 24;
        else if (mem >= 6) cap = 18;
        return Math.max(floor, Math.min(MAX_SEGMENTS, cap));
    }
    var MAX = computeMaxClips();
    try { window.__mvmMaxClips = MAX; } catch (_) {}   // exposed for telemetry / tests

    /* ── Duration contract ────────────────────────────────────────────────
       How long the finished clip is, as an explicit CHOICE — the thing this
       tool never had. /slideshow-maker has «Под длину песни» next to its total;
       here the length was whatever the arithmetic produced, and nobody could
       say which. Reported by Tatiana Zaw (06.09): «И там нет как в слайдах
       никакой кнопки подогнать… то ролик длиньше музыки, то музыка обрывается».

         song   — output == the song, to the sample. Music always plays out.
         video  — output == all the footage, played through; the song loops or
                  fades under it (this is what «по очереди целиком» always did).
         custom — a length the user typed.

       `planLaneMax` is the ceiling on GRID windows. It is a cost budget, and it
       deliberately stayed at MAX_SEGMENTS for both lanes: raising it for
       WebCodecs (tried at 128) doubled the cut rate on long songs, which is a
       re-timing of everyone's montage that the length fix does not need — and
       it cost source coverage, measured at 86.0 % against the 90 % floor
       mvm-cut-geometry guards. The contract does not depend on it: when the
       footage cannot fill a window the window is shortened and the next slot
       picks the grid back up, so a short-footage montage simply ends up with
       MORE segments than the grid had windows. */
    var planLaneMax = MAX_SEGMENTS;
    var fitMode = 'song';       // 'song' | 'video' | 'custom'
    var customSec = 0;
    /* Mode and length are separate questions with a sensible pairing: «под бит»
       tiles the song, «по очереди целиком» means the clips play through — so
       until someone states a length, the mode picks the one that matches what
       the mode NAME promises. The moment the contract is touched (this session
       or a stored one) it is the person's answer and the mode stops overriding
       it, which is what keeps «по очереди, но ровно под песню» reachable. */
    var fitTouched = false;
    function defaultFitFor(m) { return m === 'sequential' ? 'video' : 'song'; }
    function applyFitChips() {
        var fe = $('mvmFit');
        if (fe) fe.querySelectorAll('button').forEach(function (x) {
            var on = x.dataset.f === fitMode;
            x.classList.toggle('on', on);
            x.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        var cw = $('mvmCustomWrap'); if (cw) cw.classList.toggle('mvm-hidden', fitMode !== 'custom');
    }
    /* Shortest cut the eye reads as a cut rather than as a flicker. Windows
       below it are absorbed into the next one, never rendered. */
    var MIN_SEG = 0.35;

    function footageSec() {
        var s = 0;
        for (var i = 0; i < clips.length; i++) s += clips[i].duration || 0;
        return s;
    }
    function targetSec() {
        if (!song) return 0;
        if (fitMode === 'video') return Math.max(1, footageSec());
        if (fitMode === 'custom' && customSec > 0) return customSec;
        return song.duration;
    }
    /* The song only has to loop when the montage OUTLASTS it. Deriving it from
       the contract instead of a separate checkbox is the point: «под видео» +
       «не зацикливать» used to be two controls saying one thing, and the pair
       that read «песня заканчивается и начинается заново» was reachable by
       accident. The checkbox stays, but only as the answer to «зациклить или
       затихнуть», and only where the question exists. */
    function songLoops() {
        return targetSec() > (song ? song.duration : 0) + 0.25 && loopOn();
    }

    var song = null;          // { file, buffer, duration, bpm }
    var clips = [];           // { id, file, name, duration, w, h, thumbUrl }
    var mode = 'beat';        // 'beat' = beat-cut montage | 'sequential' = clips play in full, in order
    var collage = false;      // beat mode: cycle multi-panel layouts on the beat (WebCodecs only)
    var cutDensity = 'bar';
    var resultBlob = null;
    var busy = false;
    var cancelRequested = false;
    var webcodecsAbort = null; // AbortController while the hardware WebCodecs render runs
    var seq = 0;
    var lastDims = null;       // [W,H] of the finished render — result meta prints it
    var downloadedOnce = false;
    var renderStalled = false; // activity watchdog verdict — err_stalled, not «cancelled»
    var preview = null;        // MusicVideoPreview — reads the plan, never writes it

    function $(id) { return document.getElementById(id); }
    function t(k, vars) {
        var s = T[k] || k;
        if (vars) for (var key in vars) s = s.replace(new RegExp(':' + key, 'g'), vars[key]);
        return s;
    }
    function ae(type, payload) { try { if (window.__ae) window.__ae(type, payload || {}); } catch (_) {} }

    function stage(s) { try { if (window.__stage) window.__stage(s); } catch (_) {} }

    /* ───────────────────────── helpers ───────────────────────── */

    function showErr(msg) {
        var box = $('mvmErr'), tx = $('mvmErrText');
        if (box && tx) { tx.textContent = msg; box.classList.remove('mvm-hidden'); }
        try { if (window.__toast) window.__toast(msg, 'error'); } catch (_) {}
    }
    function clearErr() { var b = $('mvmErr'); if (b) b.classList.add('mvm-hidden'); }

    function fmtTime(s) {
        s = Math.max(0, Math.round(s || 0));
        var m = Math.floor(s / 60); return m + ':' + ('0' + (s % 60)).slice(-2);
    }
    function fmtSize(b) { return (b / (1024 * 1024)).toFixed(1) + ' MB'; }
    function ext(f) { return ((f && f.name || '').split('.').pop() || '').toLowerCase(); }

    // mini waveform of the loaded song (modern touch + confirms decode worked)
    function drawWave(buffer) {
        var cv = $('mvmWave'); if (!cv || !buffer) return;
        var c = cv.getContext('2d'); var W = cv.width, H = cv.height, mid = H / 2;
        c.clearRect(0, 0, W, H);
        var data = buffer.getChannelData(0), bars = 60, step = Math.max(1, Math.floor(data.length / bars)), bw = W / bars;
        var g = c.createLinearGradient(0, 0, W, 0); g.addColorStop(0, '#38bdf8'); g.addColorStop(1, '#818cf8');
        c.fillStyle = g;
        for (var i = 0; i < bars; i++) {
            var mx = 0, off = i * step;
            for (var j = 0; j < step; j += 128) { var v = Math.abs(data[off + j] || 0); if (v > mx) mx = v; }
            var h = Math.max(3, mx * H * 0.92);
            c.fillRect(i * bw + bw * 0.2, mid - h / 2, Math.max(1, bw * 0.6), h);
        }
    }

    function setProgress(p, label) {
        $('mvmProg').classList.remove('mvm-hidden');
        $('mvmBarFill').style.width = Math.max(0, Math.min(100, p)) + '%';
        if (label != null) $('mvmStatus').textContent = label;
    }

    function maybeReveal() {
        var ready = !!song && clips.length > 0;
        $('mvmSettingsWrap').classList.toggle('mvm-hidden', !ready);
        $('mvmExportWrap').classList.toggle('mvm-hidden', !ready);
        /* The fast lane's helpers (muxer, AAC door) come now, not at the press:
           on a failing network a 3 KB helper missing both attempts at export
           time sends the montage to the FFmpeg lane (comment #1937; reproduced
           on the stand 30.10 under load). Swallows its own failure; idempotent. */
        if (ready) { try { window.CanvasVideoExport && window.CanvasVideoExport.warm && window.CanvasVideoExport.warm('mp4'); } catch (_) {} }
        syncLoopRow();
        updateOutputHint();
    }

    /* Live "what you'll get" — the finished length, the estimate, and the one
       sentence that explains the plan. Also the single place the preview is
       handed the plan, so the picture, the timeline and the number on the
       button can never disagree with what the encoder will be given. */
    function updateOutputHint() {
        var el = $('mvmOutHint');
        if (!song || !clips.length) {
            if (el) el.innerHTML = '';
            if (preview) preview.setPlan([]);
            return;
        }
        var segs = planSegments(), dur = 0;
        for (var i = 0; i < segs.length; i++) dur += segs[i].dur;

        // The length lives on the action bar now, next to the control that sets
        // it — the number and the switch that changes it in one glance.
        var lenOut = $('mvmLenOut'); if (lenOut) lenOut.textContent = fmtTime(dur);
        if (preview) {
            preview.setClips(clips);
            preview.setPlan(segs);
            var wh = dims();
            preview.setAspect(wh[0], wh[1]);
        }
        applyScreenAspect();

        if (!el) return;
        var html = '';
        // What the chosen contract MEANS, in one line, instead of a bare number.
        var note = fitMode === 'video' ? 'fit_video_note' : fitMode === 'custom' ? 'fit_custom_note' : 'fit_song_note';
        html += t(note, { dur: fmtTime(dur), song: fmtTime(song.duration) });

        var est = estimateSec(dur);
        // sub-8s renders don't need a countdown — that floor is test-overridable
        var minEst = (window.__mvmEstMinSec | 0) || 8;
        if (est >= minEst) html += '<br><span class="mvm-est">' + t('est_time', { time: fmtTime(est) }) + '</span>';

        // The montage outlasts the song and the song is NOT set to loop: there
        // will be silence at the end, and that is worth saying before the render
        // rather than after it.
        if (dur > song.duration + 0.25 && !loopOn()) {
            html += '<br><span class="mvm-warn-hint">' + t('song_shorter_note', { silence: fmtTime(dur - song.duration) }) + '</span>';
        }

        // Say it up front when there is not enough footage for the song. The
        // montage can only show what it was given: 10 s of video under a 57 s
        // track means the same seconds come round five or six times, and without
        // being told, that reads as the tool malfunctioning rather than as the
        // material running out. (Report #1344: "не понял в чём фишка".)
        var haveSec = footageSec();
        if (mode !== 'sequential' && haveSec > 0 && dur > haveSec * 1.5) {
            html += '<br><span class="mvm-warn-hint">' +
                t('short_footage_hint', { times: Math.max(2, Math.round(dur / haveSec)) }) + '</span>';
        }
        el.innerHTML = html;
    }

    /* The screen shows the aspect the export will have, so «9:16» is a thing you
       SEE before committing minutes of encoding to it. */
    function applyScreenAspect() {
        var sc = $('mvmScreen'); if (!sc) return;
        var a = ($('mvmAspect') || {}).value || '16:9';
        var r = a;
        if (a === 'orig' && clips.length && clips[0].w && clips[0].h) {
            var q = clips[0].w / clips[0].h;
            r = q < 0.85 ? '9:16' : q < 1.15 ? '1:1' : '16:9';
        }
        sc.classList.toggle('r916', r === '9:16');
        sc.classList.toggle('r11', r === '1:1');
    }

    // Lock the whole input UI while a render is running (the .mvm-busy CSS dims +
    // disables everything except the progress/cancel area).
    function setBusy(b) {
        busy = b;
        var app = $('mvmApp'); if (app) app.classList.toggle('mvm-busy', b);
        var go = $('mvmGo'); if (go) go.disabled = b;
        /* Screen lock rides the busy flag, not the render body: the WebCodecs
           lane RETURNS before the FFmpeg lane's try/finally, so a release
           written into that finally would never run for the fast path — and a
           lock nobody releases outlives the work. setBusy is the one call both
           lanes make on every exit (success, cancel, stall, error).
           Why hold it at all: a phone whose screen sleeps is a phone whose tab
           the OS may discard, which is the failure this whole change is about.
           Acquired inside the Go click, so Safari's gesture requirement is met. */
        if (!window.__wakeLock) return;
        try {
            if (b) window.__wakeLock.request().then(function (how) {
                if (how && how !== 'none') ae('feature_use', { f: 'wake_lock', how: how });
            }, function () {});
            else window.__wakeLock.release();
        } catch (_) {}
    }

    /* aspect+quality -> [W,H] (even) */
    function dims() {
        var aspect = $('mvmAspect').value;
        var q = $('mvmQuality').value;
        var shortSide = q === 'high' ? 1080 : q === 'low' ? 480 : 720;
        function ev(n) { n = Math.round(n); return n % 2 ? n + 1 : n; }
        if (aspect === '1:1') return [shortSide, shortSide];
        if (aspect === '9:16') return q === 'high' ? [1080, 1920] : q === 'low' ? [480, 854] : [720, 1280];
        if (aspect === '16:9') return q === 'high' ? [1920, 1080] : q === 'low' ? [854, 480] : [1280, 720];
        // original: ratio of first clip, short side = shortSide
        var c = clips[0]; var r = (c && c.w && c.h) ? c.w / c.h : 16 / 9;
        if (r >= 1) return [ev(shortSide * r), ev(shortSide)];
        return [ev(shortSide), ev(shortSide / r)];
    }
    function crf() { var q = $('mvmQuality').value; return q === 'high' ? '20' : q === 'low' ? '27' : '23'; }
    function fadeOn() { var el = $('mvmFade'); return !!(el && el.checked); }
    // «Резкая склейка» unless the person picked «Наплыв» (the rule — music-video-transition.js).
    function transitionKind() { var el = $('mvmTransition'); return (el && el.value === 'dissolve') ? 'dissolve' : 'cut'; }
    function normOn() { var el = $('mvmNorm'); return !!(el && el.checked); }
    function duckOn() { var el = $('mvmDuck'); return !(el && !el.checked); }   // default ON when row absent
    function loopOn() { var el = $('mvmLoop'); return !(el && !el.checked); }   // default ON when row absent

    /* EBU R128 integrated loudness → gain to -14 LUFS, clamped ±12 dB.
       Shared measurement module (loudness-r128.js); absent → honest no-op.
       Measured once per song (O(n) over samples — fast, but no reason to redo). */
    function normGainFor() {
        if (!song || !song.buffer) return 1;
        if (song.normGain) return song.normGain;
        var g = 1;
        try {
            if (window.LoudnessR128 && typeof window.LoudnessR128.measureIntegratedLUFS === 'function') {
                var lufs = window.LoudnessR128.measureIntegratedLUFS(song.buffer);
                if (isFinite(lufs)) g = Math.max(0.25, Math.min(4, Math.pow(10, (-14 - lufs) / 20)));
                ae('feature_use', { f: 'loudnorm_measured', lufs: isFinite(lufs) ? Math.round(lufs * 10) / 10 : null, gain: Math.round(g * 100) / 100 });
            }
        } catch (_) {}
        song.normGain = g;
        return g;
    }

    /* Honest per-device render estimate: after every successful render we store
       ms-per-output-second for (engine × quality); the hint shows only once a
       real measurement exists — never a guess on the first run. */
    function rateKey(engine) { return 'rate_' + engine + '_' + ($('mvmQuality') ? $('mvmQuality').value : 'med'); }
    function saveRate(engine, ms, outSec) {
        if (!ms || !outSec) return;
        try { if (window.__toolSettings) window.__toolSettings.save(SLUG, rateKey(engine), String(Math.round(ms / outSec))); } catch (_) {}
    }
    /* The rate is learned on ACTIVE time (2026-09-26): a frozen tab or a
       sleeping system is not this device's work, and the last render alone
       sets the next estimate. Prod, 60 d: 14 of 1237 WebCodecs renders took
       over 10× the median time per output second (up to 90×) — each one
       would have told this device its next video takes that long. The wall
       time stays in telemetry (`ms`), the pause beside it (`paused_s`). */
    function activeMs() {
        if (typeof window.__activeNow === 'function') return window.__activeNow();
        return (window.performance && performance.now) ? performance.now() : Date.now();
    }
    function estimateSec(outSec) {
        try {
            if (!window.__toolSettings || !outSec) return 0;
            var wc = parseInt(window.__toolSettings.load(SLUG, rateKey('webcodecs'), '0'), 10) || 0;
            var ff = parseInt(window.__toolSettings.load(SLUG, rateKey('ffmpeg'), '0'), 10) || 0;
            var ffPath = clips.some(function (c) { return c.ffonly || c.keepAudio; });
            var r = ffPath ? ff : (wc || ff);
            return r ? Math.round(r * outSec / 1000) : 0;
        } catch (_) { return 0; }
    }

    /* ───────────────────────── song ───────────────────────── */

    async function onSong(file) {
        clearErr();
        $('mvmSongZone').classList.add('mvm-hidden');   // replace dropzone with the song card
        $('mvmSongBar').classList.remove('mvm-hidden');
        $('mvmSongName').textContent = file.name;
        $('mvmSongSub').textContent = t('detecting_beat');
        $('mvmBpm').classList.add('mvm-hidden');
        stage('upload'); ae('feature_use', { f: 'song_added', ext: ext(file), mb: Math.round((file.size || 0) / 1048576) });

        var Ctor = window.AudioContext || window.webkitAudioContext;
        var ctx = null;
        try {
            // Construct INSIDE try: on browsers with no Web Audio the shared SafeAC
            // stub throws AudioContextUnavailable — catch it cleanly instead of
            // leaking an unhandled rejection (real iOS Safari has Web Audio, so this
            // path is only hit on ancient/headless engines).
            try { ctx = Ctor ? new Ctor() : null; } catch (_ac) { ctx = null; }
            if (!ctx) { var e0 = new Error('no-audio-context'); e0.name = 'AudioContextUnavailable'; throw e0; }
            var res;
            if (window.AudioDecoderRobust) {
                res = await window.AudioDecoderRobust.robust(file, ctx, { slug: SLUG });
            } else {
                var ab = await file.arrayBuffer();
                res = { buffer: await ctx.decodeAudioData(ab) };
            }
            var buf = res && res.buffer;
            if (!buf) throw new Error('no-buffer');

            var bpm = 120, bpmConf = 0;
            try { var r = window.__detectBPM && window.__detectBPM(buf); if (r && r.bpm) { bpm = r.bpm; bpmConf = r.confidence || 0; } } catch (_) {}
            // Real onset timestamps (handles tempo drift better than a fixed grid);
            // falls back to the bpm grid in planSegments() when sparse/absent.
            var beats = [];
            try { if (window.__acMusicSync && window.__acMusicSync.analyzeBeats) beats = (await window.__acMusicSync.analyzeBeats(buf)) || []; } catch (_) {}
            song = { file: file, buffer: buf, duration: buf.duration, bpm: bpm, beats: beats };
            if (preview) preview.setSong(song);
            var trk0 = $('mvmTrack'); if (trk0) trk0.classList.toggle('mvm-hidden', !clips.length);

            $('mvmSongSub').textContent = fmtTime(buf.duration);
            drawWave(buf);
            // Honest tempo chip. No usable onset grid → the montage falls back
            // to an even grid, and the chip says so instead of flaunting a BPM
            // nobody detected. Detector confidence <60 is documented as
            // "treat as approximate" (bpm-detector.js) → the ≈ variant.
            var chip = $('mvmBpm');
            if (!beats || beats.length < 4) {
                chip.textContent = t('no_clear_beat');
                chip.classList.remove('mvm-hidden');
                chip.classList.add('calc');
            } else if (bpmConf && bpmConf < 60) {
                chip.textContent = '♪ ' + t('beat_approx', { bpm: Math.round(bpm) });
                chip.classList.remove('mvm-hidden');
                chip.classList.add('calc');
            } else {
                chip.textContent = '♪ ' + t('bpm_detected', { bpm: Math.round(bpm) });
                chip.classList.remove('mvm-hidden', 'calc');
            }
            ae('feature_use', { f: 'bpm_detected', v: Math.round(bpm), tier: res && res.tier, beats: beats.length });
            maybeReveal();
            persistProject();
        } catch (e) {
            song = null;
            $('mvmSongBar').classList.add('mvm-hidden');
            $('mvmSongZone').classList.remove('mvm-hidden');   // restore dropzone on failure
            var noAC = (e && e.name === 'AudioContextUnavailable') || /audio-context/i.test(String(e && e.message));
            showErr(noAC ? t('err_browser') : t('err_song_decode'));
            ae('tool_error', { error_type: noAC ? 'no_audiocontext' : 'song_decode', err_msg: String(e && e.message || e), ext: ext(file) });
        } finally { try { ctx && ctx.close(); } catch (_) {} }
    }

    function removeSong() {
        song = null;
        if (preview) { preview.pause(); preview.setSong(null); preview.setPlan([]); }
        var _tk = $('mvmTrack'); if (_tk) _tk.classList.add('mvm-hidden');
        $('mvmSongBar').classList.add('mvm-hidden');
        $('mvmSongZone').classList.remove('mvm-hidden');
        maybeReveal();
        persistProject();
    }

    /* ───────────────────────── clips ───────────────────────── */

    function probeVideo(file) {
        return new Promise(function (resolve) {
            var url = URL.createObjectURL(file);
            var v = document.createElement('video');
            v.preload = 'metadata'; v.muted = true; v.playsInline = true; v.src = url; try { v.load(); } catch (_) { }
            var done = false;
            function finish(meta) {
                if (done) return; done = true;
                // the poster is taken: the element lets go of its decoder now, not at GC
                try { v.removeAttribute('src'); v.load(); } catch (_) {}
                resolve(meta);
            }
            v.onloadedmetadata = function () {
                var dur = v.duration || 0, w = v.videoWidth || 0, h = v.videoHeight || 0;
                // ~10% in (a representative frame, not the intro/black start)
                var seekTo = dur > 0.6 ? Math.min(Math.max(1, dur * 0.1), dur - 0.2) : 0;
                function grab() {
                    var thumb = '';
                    try {
                        var cn = document.createElement('canvas');
                        cn.width = 160; cn.height = Math.round(160 * (h && w ? h / w : 0.5625)) || 90;
                        cn.getContext('2d').drawImage(v, 0, 0, cn.width, cn.height);
                        thumb = cn.toDataURL('image/jpeg', 0.62);
                    } catch (_) {}
                    URL.revokeObjectURL(url);
                    finish({ duration: dur, w: w, h: h, thumbUrl: thumb });
                }
                v.onseeked = function () {
                    // wait until the seeked frame is actually decoded/painted, else the
                    // canvas grabs a half-decoded (striped/gray) frame
                    if (v.requestVideoFrameCallback) { try { v.requestVideoFrameCallback(function () { grab(); }); return; } catch (_) {} }
                    setTimeout(grab, 70);
                };
                try { v.currentTime = seekTo; }
                catch (_) { finish({ duration: dur, w: w, h: h, thumbUrl: '' }); }
            };
            v.onerror = function () { URL.revokeObjectURL(url); finish({ duration: 0, w: 0, h: 0, thumbUrl: '', err: true }); };
            setTimeout(function () { finish({ duration: v.duration || 0, w: v.videoWidth || 0, h: v.videoHeight || 0, thumbUrl: '' }); }, 8000);
        });
    }

    /* <video> can't open every real-world clip — Android without an HEVC
       licence is the big one (an iPhone-shot .mov on a Xiaomi) — and until
       08-03 a probe miss REJECTED the clip: err_clip_decode was this tool's
       #1 error bucket, 178 events/30 d, mostly mobile. The montage engines
       don't share <video>'s limits: FFmpeg decodes HEVC/mpeg4 in software
       (measured 06-25) and WebCodecs gates itself via its own probe. So a
       probe miss falls down a metadata ladder instead of rejecting:
         tier 2 — mp4box metaOnly demux (mp4/mov/m4v): duration+dims, but it
                  reads the WHOLE file into heap (moov often sits at the end),
                  so only files ≤64 MB take this tier;
         tier 3 — FFmpeg `-i` header parse over a WORKERFS mount: lazy disk
                  reads, any container, any size. Exec exits nonzero ("no
                  output specified") by design — the header is in the log.
       Clips admitted by tier 2/3 carry ffonly:true (no thumbnail — grid shows
       the placeholder) and render through the FFmpeg pipeline. Only a file no
       tier can read is refused, exactly as before. */
    var MP4BOX_PROBE_MAX_BYTES = 64 * 1024 * 1024;
    // 12-byte ISOBMFF signature check ('ftyp' at offset 4). mp4box's BoxParser
    // walks NON-ISOBMFF bytes synchronously, logging per 8-byte window — on a
    // 40 MB junk file that blocks the main thread for minutes (measured), and
    // no Promise.race can fire while it spins. Never feed mp4box blind.
    function sniffFtyp(file) {
        return new Promise(function (resolve) {
            try {
                var fr = new FileReader();
                fr.onload = function () {
                    try {
                        var b = new Uint8Array(fr.result);
                        resolve(b.length >= 12 && b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70);
                    } catch (_) { resolve(false); }
                };
                fr.onerror = function () { resolve(false); };
                fr.readAsArrayBuffer(file.slice(0, 12));
            } catch (_) { resolve(false); }
        });
    }
    /* A still is montage material, not a degenerate video: it has no length of
       its own, so it can fill ANY beat window — which is exactly the case the
       duration contract otherwise has to compromise on. `duration` is a
       NOMINAL length used only for «под видео» and for the strip label;
       `isImage` is what the planner reads. */
    var PHOTO_NOMINAL_SEC = 3;
    function isImageFile(f) {
        return /^image\//.test(f.type || '') ||
            /\.(jpe?g|png|webp|gif|bmp|avif|heic|heif)$/i.test(f.name || '');
    }
    async function probeImage(file) {
        try {
            var bmp = await createImageBitmap(file);
            var w = bmp.width, h = bmp.height;
            // poster for the strip: the picture itself, downscaled
            var cw = 320, ch = Math.max(1, Math.round(320 * h / Math.max(1, w)));
            var cv = document.createElement('canvas'); cv.width = cw; cv.height = ch;
            cv.getContext('2d').drawImage(bmp, 0, 0, cw, ch);
            var url = '';
            try { url = cv.toDataURL('image/jpeg', 0.72); } catch (_) {}
            try { bmp.close(); } catch (_) {}
            return { duration: PHOTO_NOMINAL_SEC, w: w, h: h, thumbUrl: url, isImage: true };
        } catch (e) {
            return { duration: 0, w: 0, h: 0, thumbUrl: '', err: true };
        }
    }

    async function probeMeta(file) {
        if (isImageFile(file)) return await probeImage(file);
        var native = await probeVideo(file);
        if (!native.err && native.duration) return native;
        var vmw = window.VideoMergeWebCodecs;
        /* Tier 2a — the lazy table (2026-10-30): ftyp + moov only, so ANY size.
           The eager tier below reads the whole file and stops at 64 MB; prod,
           30 d: clips of 132 and 315 MB from Android galleries were refused
           («не удалось открыть видео») after <video> ran out of its 8 s and the
           FFmpeg tier did not come up — 10 sessions, none got a clip. A clip
           <video> did not ERROR on (it only ran out of time) stays with the
           fast lane, whose own probe asks the decoder; one it errored on goes
           to FFmpeg as before. */
        if (vmw && typeof vmw.demuxLazy === 'function' &&
            (typeof vmw.isMp4Family !== 'function' || vmw.isMp4Family(file)) && await sniffFtyp(file)) {
            try {
                if (typeof vmw.loadMp4Box === 'function') await vmw.loadMp4Box();
                var dl = await Promise.race([
                    vmw.demuxLazy(file),
                    new Promise(function (_, rej) { setTimeout(function () { rej(new Error('lazy-probe-timeout')); }, 10000); }),
                ]);
                var infl = dl && dl.info, durl = 0;
                if (infl && infl.duration && infl.timescale) durl = infl.duration / infl.timescale;
                if (!durl && dl && typeof vmw.clipDurationUs === 'function') durl = vmw.clipDurationUs(dl) / 1e6;
                var vtl = dl && dl.vtrack;
                if (durl > 0.05) {
                    ae('feature_use', { f: 'clip_probe_fallback', tier: 'lazy', ext: ext(file), mb: Math.round((file.size || 0) / 1048576), native: native.err ? 'error' : 'timeout' });
                    return { duration: durl, w: dl.displayWidth || (vtl && vtl.video && vtl.video.width) || 0,
                        h: dl.displayHeight || (vtl && vtl.video && vtl.video.height) || 0, thumbUrl: '', ffonly: !!native.err };
                }
            } catch (_) {}
        }
        if (vmw && typeof vmw.demux === 'function' &&
            (file.size || 0) <= MP4BOX_PROBE_MAX_BYTES &&
            (typeof vmw.isMp4Family !== 'function' || vmw.isMp4Family(file)) &&
            await sniffFtyp(file)) {
            try {
                if (typeof vmw.loadMp4Box === 'function') await vmw.loadMp4Box();
                // mp4box on a corrupt file can neither resolve nor reject
                // (appendBuffer throws inside FileReader.onload) — race a
                // timeout so the ladder always reaches the FFmpeg tier.
                var d = await Promise.race([
                    vmw.demux(file, true),
                    new Promise(function (_, rej) { setTimeout(function () { rej(new Error('mp4box-probe-timeout')); }, 10000); }),
                ]);
                var inf = d && d.info;
                var dur = 0;
                if (inf && inf.duration && inf.timescale) dur = inf.duration / inf.timescale;
                if (!dur && inf && inf.fragment_duration && inf.timescale) dur = inf.fragment_duration / inf.timescale;
                var vt = d && d.vtrack;
                if (dur > 0.05) {
                    ae('feature_use', { f: 'clip_probe_fallback', tier: 'mp4box', ext: ext(file) });
                    // display-, не coded-размер: портретная съёмка с телефона хранится лёжа
                    return { duration: dur, w: d.displayWidth || (vt && vt.video && vt.video.width) || 0,
                        h: d.displayHeight || (vt && vt.video && vt.video.height) || 0, thumbUrl: '', ffonly: true };
                }
            } catch (_) {}
        }
        try {
            if (window.FFmpegProcessor) {
                await window.FFmpegProcessor.init();
                var ff = window.FFmpegProcessor._getFFmpeg();
                var mnt = await window.FFmpegProcessor.mountInput(file);
                var logs = '';
                var prevCap = window.__ffLogsCapOverride;
                try {
                    // The log ring holds 40 lines by default; a multi-stream
                    // container's header dump can push Duration out of it.
                    window.__ffLogsCapOverride = 200;
                    window._ffLogs = [];
                    await ff.exec(['-hide_banner', '-i', mnt.path]);
                    logs = (window._ffLogs || []).join('\n');
                } finally {
                    window.__ffLogsCapOverride = prevCap;
                    try { await mnt.dispose(); } catch (_) {}
                }
                var dm = /Duration:\s*(\d+):(\d+):(\d+(?:\.\d+)?)/.exec(logs);
                var wh = /Video:[^\n]*?\s(\d{2,5})x(\d{2,5})/.exec(logs);
                if (dm && /Stream #[^\n]*Video/.test(logs)) {
                    var durF = (+dm[1]) * 3600 + (+dm[2]) * 60 + parseFloat(dm[3]);
                    if (durF > 0.05) {
                        ae('feature_use', { f: 'clip_probe_fallback', tier: 'ffmpeg', ext: ext(file) });
                        return { duration: durF, w: wh ? +wh[1] : 0, h: wh ? +wh[2] : 0, thumbUrl: '', ffonly: true };
                    }
                }
            }
        } catch (_) {}
        return native;   // nothing could read it — reject downstream, as before
    }

    /* The clip ceiling was worded for VIDEOS only («Максимум 12 видеороликов»),
       while 46 of the 54 identifiable hits in 30 days were batches of PHOTOS —
       ~40 snapshots at a time (chat 652, 26.09: 45 photos + 14 more, a song, no
       render, a refund request 3 minutes later). A clip is cut to the beat into
       at most MAX_SEGMENTS shots, so dozens of photos cannot all appear here by
       design; the tool that shows every photo to a song is the slideshow. Say
       how many were taken out of how many, and when photos are involved, name
       that tool and open it with one tap. */
    function sayClipCap(total, withPhotos) {
        ae('feature_use', { f: 'clip_cap_hit', cap: MAX, picked: total, photos: withPhotos ? 1 : 0 });
        if (withPhotos && CFG.slideshowUrl) {
            var msg = t('err_too_many_photos', { max: MAX, n: total });
            var box = $('mvmErr'), tx = $('mvmErrText');
            if (box && tx) { tx.textContent = msg; box.classList.remove('mvm-hidden'); }
            try {
                if (window.__toast) window.__toast(msg, 'warning', { action: { label: t('photos_to_slideshow'), onclick: function () {
                    ae('feature_use', { f: 'clip_cap_to_slideshow', cap: MAX, picked: total });
                    location.href = CFG.slideshowUrl;
                } } });
            } catch (_) {}
            return;
        }
        showErr(t('err_too_many_clips', { max: MAX, n: total }));
    }

    async function onClips(files) {
        clearErr();
        var total = clips.length + files.length, photos = 0;
        for (var p = 0; p < files.length; p++) if (isImageFile(files[p])) photos++;
        for (var i = 0; i < files.length; i++) {
            if (clips.length >= MAX) {
                sayClipCap(total, photos > 0 || clips.some(function (c) { return c.isImage; }));
                break;
            }
            var f = files[i];
            var meta = await probeMeta(f);
            if (meta.err || !meta.duration) {
                showErr(t('err_clip_decode', { name: f.name }));
                ae('tool_error', { error_type: 'clip_decode', file_ext: (f.name.split('.').pop() || '').toLowerCase() });
                continue;
            }
            var added = { id: ++seq, file: f, name: f.name, duration: meta.duration, w: meta.w, h: meta.h, thumbUrl: meta.thumbUrl, keepAudio: false, ffonly: !!meta.ffonly, isImage: !!meta.isImage };
            clips.push(added);
            // Scene profiling asks «is this moment worth cutting to?» of a
            // moving picture. A still has one moment and it is always the same
            // one, so the question does not apply.
            if (!added.isImage) profileScene(added);
        }
        ae('feature_use', { f: 'clips_added', v: clips.length });
        renderClips();
        maybeReveal();
        persistProject();
    }

    /* Photos only (no footage): the door to «Видео из аудио», whose visualiser
       draws a wave, bars or a curve over a photo — what a still under a song
       asks for and what this tool does not draw. */
    function showVizDoor() {
        var d = $('mvmVizDoor');
        if (!d) return;
        var photos = clips.length > 0 && clips.every(function (c) { return c.isImage; });
        var was = !d.classList.contains('mvm-hidden');
        d.classList.toggle('mvm-hidden', !photos);
        if (photos && !was) ae('feature_use', { f: 'viz_door_shown', v: clips.length });
    }

    // Look at what is actually in the clip, so planSegments can put its cuts on
    // real moments instead of on whatever second the arithmetic landed on.
    // Deliberately fire-and-forget: the profile is an optimisation the planner
    // uses IF it arrived, never something the render waits for. A clip dropped
    // and rendered a second later simply plans the way it always did.
    function profileScene(clip) {
        if (!window.MusicVideoScene) return;
        var mob = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        window.MusicVideoScene.profile(clip, { slug: SLUG, isMobile: mob }).then(function (p) {
            if (!p) return;
            clip.scene = p;
            renderClips();          // surfaces the "N moments picked" chip
        }).catch(function () {});
    }

    function renderClips() {
        showVizDoor();
        var wrap = $('mvmClipsWrap'), grid = $('mvmClipsGrid');
        var has = clips.length > 0;
        wrap.classList.toggle('mvm-hidden', !has);
        // The zone is the SCREEN until there is something to show: after that
        // it shrinks into the corner and keeps accepting drops, rather than
        // disappearing and leaving no way to add a second clip by dragging.
        var scr = $('mvmScreen'); if (scr) scr.classList.toggle('has-media', has);
        var trk = $('mvmTrack'); if (trk) trk.classList.toggle('mvm-hidden', !(has && song));
        $('mvmClipsCount').textContent = t('clips_count', { n: clips.length, max: MAX });
        grid.innerHTML = '';
        clips.forEach(function (c, idx) {
            var card = document.createElement('div');
            card.className = 'mvm-clip'; card.draggable = true; card.dataset.id = c.id;
            var thumb = c.thumbUrl
                ? '<img class="mvm-clip-thumb" src="' + c.thumbUrl + '" alt="">'
                : '<div class="mvm-clip-thumb placeholder">' + (c.isImage ? '🖼️' : '🎬') + '</div>';
            card.innerHTML = thumb +
                '<span class="mvm-clip-idx">' + (idx + 1) + '</span>' +
                '<button class="mvm-clip-x" data-id="' + c.id + '" aria-label="' + esc(T.clip_remove || 'remove') + '" title="' + (T.clip_remove || '') + '">✕</button>' +
                // A still's «duration» is a nominal number the planner uses for
                // «под видео»; printing it as 0:03 would read as a fact about
                // the file, which it is not.
                '<div class="mvm-clip-foot"><span class="mvm-clip-dur">' + (c.isImage ? t('photo_label') : fmtTime(c.duration)) + '</span>' +
                '<div class="mvm-clip-actions">' +
                '<button class="mvm-clip-mv" data-mv="-1" data-id="' + c.id + '" aria-label="' + esc(T.clip_move_left || 'move left') + '"' + (idx === 0 ? ' disabled' : '') + '>‹</button>' +
                '<button class="mvm-clip-mv" data-mv="1" data-id="' + c.id + '" aria-label="' + esc(T.clip_move_right || 'move right') + '"' + (idx === clips.length - 1 ? ' disabled' : '') + '>›</button>' +
                // A still has no sound to keep — offering the switch would be
                // a control that cannot do anything.
                (c.isImage ? '' :
                    '<button class="mvm-clip-audio' + (c.keepAudio ? ' on' : '') + '" data-id="' + c.id + '" ' +
                    'title="' + (T.clip_keep_audio || '') + '">' + (c.keepAudio ? '🔊' : '🔇') + '</button>') +
                '</div></div>' +
                // per-clip volume — only meaningful while the clip keeps its own
                // sound; the ducked music dips under exactly this signal
                '<div class="mvm-clip-volrow' + (c.keepAudio ? '' : ' mvm-hidden') + '" data-id="' + c.id + '">' +
                '<span aria-hidden="true">🔊</span>' +
                '<input type="range" class="mvm-clip-vol" data-id="' + c.id + '" min="0" max="150" step="5" value="' + Math.round((c.gain == null ? 1 : c.gain) * 100) + '" ' +
                'title="' + (T.clip_volume || '') + '" aria-label="' + (T.clip_volume || '') + '"></div>';
            grid.appendChild(card);
        });
        // trailing "+" add tile — small picker that grows the row rightward
        if (has && clips.length < MAX) {
            var tile = document.createElement('div');
            tile.className = 'mvm-addtile';
            tile.innerHTML = '<span class="mvm-plus">+</span><span>' + (T.add_more_clips || '') + '</span>';
            tile.addEventListener('click', function () {
                var input = document.querySelector('[data-uploader-for=mvmClips]');
                if (input) input.click();
            });
            grid.appendChild(tile);
        }
        // remove buttons
        grid.querySelectorAll('.mvm-clip-x').forEach(function (b) {
            b.addEventListener('click', function () {
                var id = +b.dataset.id;
                clips = clips.filter(function (c) { return c.id !== id; });
                renderClips(); maybeReveal();
                persistProject();
            });
        });
        // keep-audio toggles (per-clip: keep original sound, music ducks under it)
        grid.querySelectorAll('.mvm-clip-audio').forEach(function (b) {
            b.addEventListener('click', function (e) {
                e.stopPropagation();
                var id = +b.dataset.id;
                var c = clips.find(function (x) { return x.id === id; });
                if (!c) return;
                c.keepAudio = !c.keepAudio;
                b.classList.toggle('on', c.keepAudio);
                b.textContent = c.keepAudio ? '🔊' : '🔇';
                var vr = grid.querySelector('.mvm-clip-volrow[data-id="' + id + '"]');
                if (vr) vr.classList.toggle('mvm-hidden', !c.keepAudio);
                updateAudioRows();
                ae('feature_use', { f: 'clip_keep_audio', v: c.keepAudio ? 1 : 0 });
            });
        });
        // per-clip volume (kept-audio clips): 0–150 %, feeds the extraction
        // `volume=` in the FFmpeg mux (keep-audio renders always run there)
        grid.querySelectorAll('.mvm-clip-vol').forEach(function (r) {
            r.addEventListener('input', function () {
                var c = clips.find(function (x) { return x.id === +r.dataset.id; });
                if (c) c.gain = (parseInt(r.value, 10) || 0) / 100;
            });
            r.addEventListener('change', function () {
                ae('feature_use', { f: 'clip_volume', v: parseInt(r.value, 10) || 0 });
            });
            // a range inside a draggable card: drag must not hijack the slider
            r.addEventListener('pointerdown', function (e) { e.stopPropagation(); });
        });
        updateAudioRows();
        // reorder via buttons — works on touch / iPhone where HTML5 drag does not fire
        grid.querySelectorAll('.mvm-clip-mv').forEach(function (b) {
            b.addEventListener('click', function (e) {
                e.stopPropagation();
                var id = +b.dataset.id, dir = +b.dataset.mv;
                var i = clips.findIndex(function (c) { return c.id === id; });
                var j = i + dir;
                if (i < 0 || j < 0 || j >= clips.length) return;
                var tmp = clips[i]; clips[i] = clips[j]; clips[j] = tmp;
                renderClips();
                ae('feature_use', { f: 'clip_reorder', via: 'button' });
            });
        });
        wireDrag(grid);
    }

    // The ducking row is noise until some clip actually keeps its sound.
    function updateAudioRows() {
        var anyKept = clips.some(function (c) { return c.keepAudio; });
        var dr = $('mvmDuckRow'); if (dr) dr.classList.toggle('mvm-hidden', !anyKept);
    }

    function wireDrag(grid) {
        var dragId = null;
        grid.querySelectorAll('.mvm-clip').forEach(function (el) {
            el.addEventListener('dragstart', function () { dragId = +el.dataset.id; el.classList.add('dragging'); });
            el.addEventListener('dragend', function () { el.classList.remove('dragging'); dragId = null; });
            el.addEventListener('dragover', function (e) { e.preventDefault(); });
            el.addEventListener('drop', function (e) {
                e.preventDefault();
                var targetId = +el.dataset.id;
                if (dragId == null || dragId === targetId) return;
                var from = clips.findIndex(function (c) { return c.id === dragId; });
                var to = clips.findIndex(function (c) { return c.id === targetId; });
                if (from < 0 || to < 0) return;
                var moved = clips.splice(from, 1)[0];
                clips.splice(to, 0, moved);
                renderClips();
            });
        });
    }

    /* ───────────────────── segment planning ───────────────────── */

    // How many NON-OVERLAPPING windows of `segLen` seconds a clip of `dur`
    // seconds holds. This is the hard ceiling on how many distinct moments the
    // clip can show; asking for more is what makes a montage replay itself.
    function distinctWindows(dur, segLen) {
        if (!(dur > 0) || !(segLen > 0)) return 1;
        return Math.max(1, Math.floor(dur / segLen));
    }

    // Where a clip's `span` windows of `winLen` seconds should start, inside
    // [0, usable]. Without a scene profile this is the even end-to-end spread
    // the tool has always used. With one, the windows are spread evenly over the
    // clip's LIVE material instead of over wall-clock time, so a black or frozen
    // stretch stops earning cuts.
    //
    // The weight is deliberately a GATE (live vs dead), not a taste score. An
    // earlier version weighted by how interesting each moment looked, and it
    // pulled windows toward high-motion passages on footage that was fine
    // everywhere — measured: multi-clip coverage fell 98.9 % → 88.6 % for no
    // stated benefit. Ranking moments by interest is a guess; "there is nothing
    // in this frame" is a fact. With a gate, a clip that is alive throughout
    // plans EXACTLY as it did before this existed.
    function windowStarts(clip, span, winLen, usable, enforceDisjoint) {
        var i, starts = [];
        if (span <= 1) return [usable * 0.05];
        var p = clip && clip.scene, scene = window.MusicVideoScene;
        if (!p || !scene) {
            for (i = 0; i < span; i++) starts.push(usable * i / (span - 1));
        } else {
            var N = 200, dt = usable / N, cum = [0];
            for (i = 0; i < N; i++) {
                // 1 for live material, a sliver for dead — the sliver keeps the
                // curve strictly increasing, so even an entirely dead clip still
                // yields a spread instead of stacking every window at zero
                var live = scene.qualityAt(p, i * dt + dt / 2) > 0.02 ? 1 : 0.02;
                cum.push(cum[i] + live * dt);
            }
            var mass = cum[N] || 1;
            for (i = 0; i < span; i++) {
                var target = mass * i / (span - 1), j = 0;
                while (j < N && cum[j + 1] < target) j++;
                starts.push(Math.min(usable, j * dt));
            }
        }
        // Disjointness is a hard guarantee where it MATTERS — a clip the viewer
        // sees back to back, whose span was capped to the windows it actually
        // holds. Applying it to a clip that is interleaved with others would be
        // actively harmful: there `span` is the number of slices, which may far
        // exceed the clip's capacity, and clamping would stack every window at
        // the end instead of spreading finely across the clip (measured: it cost
        // the 4-clip montage 98.9 % → 88.6 % coverage). Overlap between windows
        // the viewer never sees consecutively is variety, not judder.
        if (enforceDisjoint) {
            for (i = 1; i < span; i++) if (starts[i] < starts[i - 1] + winLen) starts[i] = starts[i - 1] + winLen;
            for (i = span - 1; i >= 0; i--) {
                if (starts[i] > usable) starts[i] = usable;
                if (i > 0 && starts[i] < starts[i - 1] + winLen) starts[i - 1] = Math.max(0, starts[i] - winLen);
            }
        } else {
            for (i = 0; i < span; i++) starts[i] = Math.max(0, Math.min(usable, starts[i]));
        }
        return starts;
    }

    // Visiting order for those windows, van der Corput (bit-reversal): 4 windows
    // → [0,2,1,3], 6 → [0,4,2,1,5,3]. Consecutive entries are far apart, so a
    // clip that must be revisited cuts to a DIFFERENT part of itself each time
    // instead of playing straight through (which would put the cut on a frame
    // identical to the previous one — a beat montage whose beats are invisible).
    var _spreadCache = {};
    function spreadOrder(n) {
        if (_spreadCache[n]) return _spreadCache[n];
        var idx = [], i;
        for (i = 0; i < n; i++) idx.push(i);
        idx.sort(function (a, b) { return radicalInverse(a) - radicalInverse(b) || a - b; });
        return (_spreadCache[n] = idx);
    }
    function radicalInverse(i) {
        var r = 0, f = 0.5;
        while (i > 0) { r += (i & 1) * f; i >>= 1; f *= 0.5; }
        return r;
    }

    /* Boundary timestamps of the beat grid over [0, target]. Real onset times
       (from __acMusicSync) track tempo drift; the bpm grid is the fallback when
       beats are sparse. When the target runs PAST the song (fit-to-footage, or
       a custom length), the grid keeps going at the same pulse instead of
       stopping at the last onset — otherwise the tail of a long montage would
       be one enormous window. */
    function beatBounds(target, mult) {
        var beats = (song.beats && song.beats.length >= 4) ? song.beats : null;
        var bounds = [], i;
        if (beats) {
            for (i = 0; i < beats.length; i += mult) if (beats[i] < target - 0.15) bounds.push(beats[i]);
            if (!bounds.length || bounds[0] > 0.05) bounds.unshift(0);
        } else {
            var step0 = (60 / (song.bpm || 120)) * mult;
            for (var t0 = 0; t0 < target - 0.15; t0 += step0) bounds.push(t0);
        }
        var pulse = bounds.length > 1
            ? (bounds[bounds.length - 1] - bounds[0]) / (bounds.length - 1)
            : (60 / (song.bpm || 120)) * mult;
        if (!(pulse > 0.05)) pulse = 1;
        var last = bounds.length ? bounds[bounds.length - 1] : 0;
        var guard = 0;
        while (last + pulse < target - 0.15 && guard++ < 20000) { last += pulse; bounds.push(last); }
        bounds.push(target);
        // Cap the montage by THINNING the grid (keep every Nth boundary) so the
        // windows still tile the whole target. Slicing the first N bounds and
        // appending the end — what this did until 2026-07-10 — glued the whole
        // tail onto the final window.
        var laneMax = planLaneMax;
        if (bounds.length - 1 > laneMax) {
            var stride = Math.ceil((bounds.length - 1) / laneMax);
            var thin = [];
            for (var b = 0; b < bounds.length - 1; b += stride) thin.push(bounds[b]);
            thin.push(target);
            bounds = thin;
        }
        return bounds;
    }

    function planSegments() {
        var target = targetSec();
        if (!(target > 0.05) || !clips.length) return [];

        // Sequential mode: clips play IN FULL, in order — no beat cutting. The
        // sequence repeats until the target is filled and the last one is cut
        // exactly ON it, so «под песню» is exact here too. With fit-to-footage
        // the target IS the footage sum, so every clip plays once, in full —
        // the behaviour this mode always had.
        if (mode === 'sequential') {
            var out = [], tt = 0, si = 0, sg = 0;
            while (tt < target - 0.05 && sg++ < 6000) {
                var sc = clips[si % clips.length];
                var sd = Math.min(sc.duration || 0, target - tt);
                if (sd < 0.05) { si++; if (si > clips.length * 4) break; continue; }
                out.push({ clipIndex: si % clips.length, start: 0, dur: +sd.toFixed(3), keepAudio: !!sc.keepAudio });
                tt += sd; si++;
            }
            return out;
        }
        var mult = cutDensity === 'beat' ? 1 : cutDensity === '2bar' ? 8 : 4; // bar = 4 beats
        var bounds = beatBounds(target, mult);

        /* ⚠️⚠️ THE duration contract, and the one line this tool got wrong for
           its whole life. A window is only ever handed to footage that can FILL
           it: when the round-robin pick is shorter than the window, THE WINDOW
           is shortened to the clip and the next slot runs to the SAME grid
           boundary — a short pickup cut that lands the montage back on the
           beat. What it did before was the opposite: keep the window and
           truncate the SEGMENT down to the clip's length instead, so
           the montage silently came out shorter than the song and the mux then
           cut the music to it. Measured over 90 days: 126 of 514 renders
           (24.5 %) delivered a clip shorter than its song, on average by 75.9 s
           — reported as «то ролик длиньше музыки, то музыка обрывается»
           (Tatiana Zaw, 06.09). Σ segLens == target, exactly, always.
           Oracle: tests/playwright/mvm-duration-contract.mjs. */
        var assign = [], segLens = [], uses = {}, longest = {};
        var t = 0, rr = 0, bi = 0, guard = 0;
        while (t < target - 0.05 && guard++ < 6000) {
            while (bi < bounds.length && bounds[bi] <= t + 0.12) bi++;
            var nb = Math.min(bi < bounds.length ? bounds[bi] : target, target);
            /* Absorb a stub window into the next one instead of cutting on it.
               A grid whose first onset is at 0.14 s, or whose last window is a
               remainder, otherwise produces a single-frame flash at the head of
               the montage that reads as a glitch, not as an edit. */
            while (nb - t < MIN_SEG && bi + 1 < bounds.length) { bi++; nb = Math.min(bounds[bi], target); }
            var L = nb - t;
            if (L < 0.12) { bi++; continue; }
            var pick = -1;
            for (var q = 0; q < clips.length; q++) {
                var cand = (rr + q) % clips.length;
                // A still has no length: it fills whatever window it is given.
                if (clips[cand].isImage || (clips[cand].duration || 0) >= L - 1e-3) { pick = cand; break; }
            }
            if (pick < 0) {
                // No footage anywhere is this long: cut on the clip instead of
                // on the beat, and let the next slot pick the grid back up.
                pick = rr % clips.length;
                if (!clips[pick].isImage) {
                    L = Math.max(0.25, Math.min(L, clips[pick].duration || 0.25));
                    if (t + L > target) L = target - t;
                }
            }
            rr = pick + 1;
            assign.push(pick); segLens.push(L);
            uses[pick] = (uses[pick] || 0) + 1;
            if (L > (longest[pick] || 0)) longest[pick] = L;
            t += L;
        }
        if (!segLens.length && clips.length) {
            assign = [0]; segLens = [Math.min(target, clips[0].duration || target)];
            uses = { 0: 1 }; longest = { 0: segLens[0] };
        }
        var rem = target - t;
        if (rem > 0.002 && segLens.length) {
            var lastI = segLens.length - 1, lastC = clips[assign[lastI]];
            if (lastC && (lastC.duration || 0) >= segLens[lastI] + rem - 1e-3) {
                segLens[lastI] += rem;
                if (segLens[lastI] > (longest[assign[lastI]] || 0)) longest[assign[lastI]] = segLens[lastI];
            }
        }
        // Which clips the viewer sees BACK TO BACK. Overlapping windows only read
        // as juddering when the same footage runs across a cut; when the next
        // segment is a different clip, near-neighbour windows are just extra
        // variety, so those clips keep the fine end-to-end spread untouched.
        // With round-robin assignment this set is non-empty only for a single
        // clip — which is 41 % of all renders (301 of 727, 30 d, 2026-08-10).
        var backToBack = {};
        for (var k1 = 1; k1 < assign.length; k1++) if (assign[k1] === assign[k1 - 1]) backToBack[assign[k1]] = true;

        // Build segments. KEY: spread each clip's slices EVENLY across its WHOLE
        // duration, so a long source video (e.g. 10 min) is sampled end-to-end
        // instead of only showing its first few seconds.
        //
        // ⚠️ That even spread is only valid while the clip HAS enough material.
        // The spread puts starts `(D - L)/(uses - 1)` apart, but each slice shows
        // `L` seconds — so once `uses * L > D` consecutive slices OVERLAP, and the
        // montage steps BACKWARDS at every cut. Measured on the shape from report
        // #1344 (one 10 s clip under a 57 s song): 27 of 27 cuts jumped ~1.7 s
        // back and 88 % of the output was footage the viewer had just watched.
        // That is what "кадры дёргаются" is — not a decode fault; the render was
        // clean, hardware, 15 s. See tests/playwright/mvm-cut-geometry.mjs.
        //
        // Per-clip window plan: WHICH windows exist, and IN WHAT ORDER they are
        // visited. Two separate questions, and both have to be right for a beat
        // montage to read as one.
        //
        //  · WHICH — a clip holds at most floor(D / L) disjoint windows of length
        //    L. Ask for more and consecutive windows overlap: the montage steps
        //    backwards into footage just shown (report #1344, 27 of 27 cuts, 88 %
        //    of the output replayed).
        //
        //  · ORDER — walking the windows in time order advances the source by
        //    (step − L) per cut. Whenever that is smaller than a window, the
        //    montage is really the clip playing through with a hitch on every
        //    beat: nothing repeats, and it still looks broken. Measured across
        //    the realistic range, forward order only produces a readable cut once
        //    the clip is about TWICE the song — 4 min of footage for a 2 min
        //    track. Below that (which is nearly every single-clip render) the
        //    windows are visited in spread order instead, so each cut lands a
        //    window-length or more away.
        //
        // Threshold is `jump ≥ one window`: scale-free, tied to the montage's own
        // rhythm, no magic constant. Real-footage backing (tests/playwright/
        // mvm-cut-visibility.mjs): a 1–2 s jump reads as a 4–5× change against a
        // clip's own motion floor, and the curve saturates past that.
        var plans = {};
        for (var ci2 = 0; ci2 < clips.length; ci2++) {
            if (!uses[ci2]) continue;
            var cl = clips[ci2], wl = longest[ci2] || segLens[0] || 1;
            var d0 = Math.min(wl, cl.duration);
            var us0 = Math.max(0, cl.duration - d0);
            var win = backToBack[ci2] ? distinctWindows(cl.duration, wl) : uses[ci2];
            var sp = Math.min(uses[ci2], win);
            var step = sp > 1 ? us0 / (sp - 1) : us0;
            // Interleaved clips never need this: the next segment is different
            // footage, so the cut is visible however close the windows sit.
            var spread = !!backToBack[ci2] && step < 2 * d0;
            if (window.__mvmForceOrder === 'forward') spread = false;
            if (window.__mvmForceOrder === 'spread') spread = !!backToBack[ci2];
            plans[ci2] = {
                span: sp, windows: win, spread: spread,
                starts: windowStarts(cl, sp, d0, us0, !!backToBack[ci2])
            };
        }

        var seen = {}, segs = [];
        for (var k2 = 0; k2 < segLens.length; k2++) {
            var idx = assign[k2], clip = clips[idx], segLen = segLens[k2];
            var u = seen[idx] = (seen[idx] || 0) + 1;      // 1-based: which use of this clip
            // The window, verbatim — NOT clamped to the clip. The fit is
            // decided when the window is CUT (see the duration contract
            // above), so by here it already fits its footage, and a clamp
            // here could only reintroduce the silent shortfall.
            var dur = segLen;
            var plan = plans[idx];
            var slot = plan.spread
                ? spreadOrder(plan.span)[(u - 1) % plan.span]
                : Math.min(u - 1, plan.span - 1);
            var start = plan.starts[Math.min(slot, plan.starts.length - 1)];
            if (!(start >= 0)) start = 0;
            segs.push({ clipIndex: idx, start: +start.toFixed(3), dur: +dur.toFixed(3), keepAudio: !!clip.keepAudio });
        }

        // Collage mode: turn selected beat segments into multi-panel LAYOUTS
        // (full / side / pip / 3-column / 2×2) that cycle on the beat — the
        // dynamic split-screen look. This is additive metadata: seg.clipIndex /
        // seg.start stay the primary cell (cells[0]) so the FFmpeg fallback,
        // which ignores layout/cells, still renders a valid single-clip montage.
        // Only the WebCodecs engine composites the panels.
        if (collage && clips.length >= 2) {
            // A layout lands on a subset of beats; full-frame shots dominate so
            // the collages read as accents (the reference-video rhythm).
            var PATTERN = ['full', 'full', 'side', 'full', 'row3', 'full', 'grid', 'pip'];
            var LN = { full: 1, side: 2, pip: 2, row3: 3, grid: 4 };
            var n2 = clips.length;
            // Pass A — pick each segment's layout + its cells' clip indices, and
            // count every clip's total on-screen appearances (primary + panels).
            var plans = [], apUses = {};
            for (var s2 = 0; s2 < segs.length; s2++) {
                var want = PATTERN[s2 % PATTERN.length];
                var need = Math.min(LN[want], n2);
                var lay = need <= 1 ? 'full'
                    : need === 2 ? (want === 'pip' ? 'pip' : 'side')
                    : need === 3 ? 'row3' : 'grid';
                var base = segs[s2].clipIndex, cis = [];
                for (var cc = 0; cc < need; cc++) {
                    var ci2 = (base + cc) % n2;
                    cis.push(ci2); apUses[ci2] = (apUses[ci2] || 0) + 1;
                }
                plans.push({ layout: lay, cis: cis });
            }
            // Pass B — place each clip's appearances. A panel is a window into a
            // clip exactly like a full-frame segment is, so it obeys the same two
            // rules and uses the same code: a clip can only show floor(D / L)
            // distinct windows however many panels it appears in, and when its
            // appearances would otherwise creep forward in near-identical steps
            // they are visited in spread order instead.
            //
            // ⚠️ This used to be its own copy of the even-spread formula, and the
            // copy is why it kept replaying itself after the montage path was
            // fixed: a clip appearing in ~22 panels of a 24-segment collage needs
            // ~52 s of material, so any ordinary phone clip overlapped every time.
            // Reusing windowStarts/spreadOrder is the point, not a shortcut.
            var apLongest = {}, apBack = {};
            for (var q1 = 0; q1 < segs.length; q1++) {
                for (var q2 = 0; q2 < plans[q1].cis.length; q2++) {
                    var qc = plans[q1].cis[q2];
                    if (segs[q1].dur > (apLongest[qc] || 0)) apLongest[qc] = segs[q1].dur;
                    // appears again in the very next segment → the viewer sees the
                    // two windows back to back, in whichever panels they land
                    if (q1 + 1 < segs.length && plans[q1 + 1].cis.indexOf(qc) >= 0) apBack[qc] = true;
                }
            }
            var apPlan = {};
            for (var q3 = 0; q3 < clips.length; q3++) {
                if (!apUses[q3]) continue;
                var qd = clips[q3], qwl = apLongest[q3] || segLens[0] || 1;
                var qd0 = Math.min(qwl, qd.duration);
                var qus = Math.max(0, qd.duration - qd0);
                var qwin = apBack[q3] ? distinctWindows(qd.duration, qwl) : apUses[q3];
                var qsp = Math.min(apUses[q3], qwin);
                var qstep = qsp > 1 ? qus / (qsp - 1) : qus;
                apPlan[q3] = {
                    span: qsp, windows: qwin,
                    spread: !!apBack[q3] && qstep < 2 * qd0,
                    starts: windowStarts(qd, qsp, qd0, qus, !!apBack[q3])
                };
            }
            var apSeen = {};
            for (var s3 = 0; s3 < segs.length; s3++) {
                var seg = segs[s3], pl = plans[s3], cells = [];
                for (var cc2 = 0; cc2 < pl.cis.length; cc2++) {
                    var ci3 = pl.cis[cc2];
                    var uu = apSeen[ci3] = (apSeen[ci3] || 0) + 1;
                    var ap = apPlan[ci3];
                    var qslot = ap.spread
                        ? spreadOrder(ap.span)[(uu - 1) % ap.span]
                        : Math.min(uu - 1, ap.span - 1);
                    var st = ap.starts[Math.min(qslot, ap.starts.length - 1)];
                    cells.push({ clipIndex: ci3, start: +Math.max(0, st || 0).toFixed(3) });
                }
                seg.layout = pl.layout;
                seg.cells = cells;
                seg.clipIndex = cells[0].clipIndex;   // keep primary in sync for FFmpeg
                seg.start = cells[0].start;
            }
        }
        // The plan is what decides whether the montage reads as a montage, and
        // it is invisible in the output: a test can see the pixels but not which
        // source window each one came from. Exposing it lets the guards assert on
        // the cut geometry directly. Read-only, same family as __mvmMaxClips.
        try { window.__mvmLastPlan = segs; } catch (_) {}
        return segs;
    }

    /* ───────────────────────── render ───────────────────────── */

    async function render() {
        if (busy) return;
        if (!song) { showErr(t('err_no_song')); return; }
        if (!clips.length) { showErr(t('err_no_clips')); return; }
        if (!window.FFmpegProcessor) { showErr(t('err_wasm')); return; }

        /* Soft daily cap. Gated below the three cheap bails (no song, no clips, no
           FFmpeg) so a refused render costs nothing, and above setBusy so a wall-hit
           can't leave the UI locked. commit() when the clip is on screen; abort() in
           the finally, which covers the catch — including the cancel path, which
           reaches it as an error classed 'cancelled'. Not billed on download. */
        var myRun = window.__toolLimit ? window.__toolLimit.begin('music-video-maker') : null;
        if (window.__toolLimit && !myRun) return;   // wall hit — note already shown

        setBusy(true); cancelRequested = false; renderStalled = false; clearErr();
        $('mvmResult').classList.add('mvm-hidden');
        setProgress(2, t('status_decoding'));
        var renderT0 = (window.performance && performance.now) ? performance.now() : 0;
        var renderA0 = activeMs();
        // R128 normalization folds into ONE gain factor applied by both engines'
        // volume stage — measured once per song, only when the toggle is on.
        var songGain = normOn() ? normGainFor() : 1;

        /* Activity watchdog. A healthy encode is chatty — ffmpeg prints frame=
           lines continuously, the WebCodecs path calls onProgress per frame.
           120 s of TOTAL silence while busy = the engine is wedged (worker died
           mid-exec, hw encoder hung): until now that was an infinite
           «Рендерим клип…». Kill the instance and say so honestly (err_stalled).
           window.__mvmStallMs overrides the window in tests; __mvmStallFreeze
           lets a test simulate silence without breaking the processor's log pipe.

           ⚠️⚠️ Liveness is `window._ffLogSeq` (monotonic), NOT `_ffLogs.length`.
           _ffLogs is a RING capped at 20 — after the first 20 lines its length is
           permanently 20, so the old check went blind and every render slower
           than the window was killed WHILE WORKING. Measured 2026-08-08 on a
           healthy 51.6 s render that produced a valid file: log content never
           paused >1.0 s, but .length sat unchanged 19.5 s (it moved only when a
           heap-recycle reset the ring) — i.e. any device ~6× slower than that
           desktop hit a false «Обработка зависла». That is the reported failure:
           a paying iPhone user burned 5 days and 3 renders/session on 2 clips,
           each killed at exactly silent_ms:120000 while ffmpeg was progressing. */
        var stallMs = (window.__mvmStallMs | 0) || 120000;
        /* Silence counts only while the page is watched AND can run (2026-09-26).
           Hidden: the canvas lane draws from <video> elements and Chromium does
           not decode media in a hidden tab, so the render stands still by design
           and goes on when the person is back; a frozen phone or a sleeping
           laptop stops the engine as well. The wall clock read all of it as a
           wedge — prod, 60 d: 13 of 29 classifiable kills came after the person
           had left the page (12 on phones), none preceded by the shared guard's
           warning about VISIBLE silence. So: the page's active time
           (__activeNow — freezes and sleep taken out), no checks while hidden,
           a fresh start on return — the rule __withStallGuard already keeps. */
        var wdNow = function () { return (typeof window.__activeNow === 'function') ? window.__activeNow() : Date.now(); };
        window.__mvmLastActivity = wdNow();
        var stallSeq = -1;
        var onStallVis = function () { if (!document.hidden) window.__mvmLastActivity = wdNow(); };
        document.addEventListener('visibilitychange', onStallVis);
        var stopStallTimer = function () {
            if (stallTimer) { clearInterval(stallTimer); stallTimer = null; }
            document.removeEventListener('visibilitychange', onStallVis);
        };
        var stallTimer = setInterval(function () {
            try {
                if (document.hidden) { window.__mvmLastActivity = wdNow(); return; }
                var seq = window._ffLogSeq;
                if (!window.__mvmStallFreeze && typeof seq === 'number' && seq !== stallSeq) {
                    stallSeq = seq;
                    window.__mvmLastActivity = wdNow();
                }
                if (wdNow() - window.__mvmLastActivity > stallMs) {
                    stopStallTimer();
                    renderStalled = true;
                    window.__mvmStallFired = Date.now();   // observability (tests + prod debugging)
                    ae('tool_error', { error_type: 'stalled', silent_ms: stallMs, clips: clips.length });
                    cancelRequested = true;
                    if (webcodecsAbort) {
                        // WC path: abort() rejects the render promise → the normal
                        // catch surfaces err_stalled (renderStalled wins there).
                        try { webcodecsAbort.abort(); } catch (_) {}
                    } else {
                        // FFmpeg path: terminating a wedged worker leaves the
                        // in-flight exec() promise DANGLING — nothing ever
                        // rejects, so the render()'s own catch can never run
                        // (measured: the page sat busy forever). Surface the
                        // verdict HERE and release the UI; the parked await is
                        // orphaned but its worker is gone, and a new render
                        // starts cleanly on the reinit'd instance.
                        try { if (window.FFmpegProcessor && window.FFmpegProcessor.reinit) window.FFmpegProcessor.reinit(); } catch (_) {}
                        $('mvmProg').classList.add('mvm-hidden');
                        showErr(t('err_stalled'));
                        stage('error');
                        ae('tool_error', { error_type: 'stalled_surfaced', err_msg: 'ffmpeg_exec_dangling', clips: clips.length });
                        if (myRun) myRun.abort();
                        setBusy(false);
                    }
                }
            } catch (_) {}
        // tick at half the window (≤5 s): a window shorter than the tick would
        // otherwise never be checked before a fast render clears the timer
        }, Math.max(1000, Math.min(5000, Math.floor(stallMs / 2))));
        var clearStall = stopStallTimer;
        stage('process');
        ae('feature_use', {
            f: 'render_start', clips: clips.length, density: cutDensity,
            // How much footage there is against how much the song needs. Without
            // this pair a report of "кадры дёргаются" cannot be placed: too
            // little material replays, and slightly-too-much stutters, and the
            // two look nothing alike but are indistinguishable in telemetry.
            // #1344 took two passes to diagnose for exactly this reason.
            srcSec: Math.round(clips.reduce(function (a, c) { return a + (c.duration || 0); }, 0)),
            photos: clips.filter(function (c) { return c.isImage; }).length,
            songSec: Math.round((song && song.duration) || 0),
            aspect: $('mvmAspect').value, quality: $('mvmQuality').value,
            effect: ($('mvmEffect') || {}).value || 'none',
            tx: transitionKind(),
            layout: collage ? 'collage' : 'single',
            kept: clips.filter(function (c) { return c.keepAudio; }).length,
            cores: navigator.hardwareConcurrency || 0, devmem: navigator.deviceMemory || 0
        });

        /* ── Hardware WebCodecs montage (PRIMARY). Replaces up-to-48 single-thread
           libx264 exec()s with one hw decode→composite→encode pass — the fix for
           the 29 %-completion / 8-57 min-render root cause (telemetry 2026-07).
           Falls through to the FFmpeg pipeline below on any probe miss or
           mid-encode failure; keep-audio (tier-2) renders always use FFmpeg. */
        // ffonly clips (admitted by the mp4box/FFmpeg probe ladder — codecs
        // <video> can't decode here) skip the WebCodecs branch outright: its
        // own probe would reject them anyway, after reading files into heap.
        var anyFfOnly = clips.some(function (c) { return c.ffonly; });
        /* The page's own <video> elements let go of their decoders while the
           montage needs them (2026-10-30). The preview keeps one element per
           clip with a decoded frame, the last result keeps another; on Android
           each can hold a hardware decoder, and the montage opens a fresh one
           for every segment next to a hardware encoder — the device's codec
           count is shared. They come back on the next look at the preview. */
        try { if (preview && preview.releaseMedia) preview.releaseMedia(); } catch (_) {}
        try { var rv0 = $('mvmResultVideo'); if (rv0 && rv0.getAttribute('src')) { rv0.pause(); rv0.removeAttribute('src'); rv0.load(); } } catch (_) {}
        if (window.MusicVideoWebCodecs && !anyFfOnly) {
            var segsPlan = planSegments();
            var anyKeptWc = segsPlan.some(function (s) { return s.keepAudio; });
            if (!anyKeptWc) {
                var WHwc = dims();
                try {
                    var wcProbe = await window.MusicVideoWebCodecs.probe(clips,
                        { W: WHwc[0], H: WHwc[1], fps: 30, crf: +crf(),
                          audioRate: (song && song.buffer && (song.buffer.sampleRate === 44100 || song.buffer.sampleRate === 48000)) ? song.buffer.sampleRate : 48000 });
                    /* «Прервать» pressed while the probe ran: there was no
                       controller to abort yet, and the render used to start
                       anyway and run to the end (stand F3, 30.10 — the probe
                       now asks the encoder for H.264 and reads each clip's moov,
                       which widened the window). The press is honoured here. */
                    if (cancelRequested) {
                        clearStall();
                        handleRenderError(mark(new Error('cancelled'), 'cancelled'));
                        if (myRun) myRun.abort();
                        setBusy(false);
                        return;
                    }
                    if (wcProbe.ok) {
                        var totalWc = 0; segsPlan.forEach(function (s) { totalWc += s.dur; });
                        webcodecsAbort = new AbortController();
                        setProgress(6, t('status_rendering', { i: 0, n: segsPlan.length }));
                        lastDims = WHwc;
                        resultBlob = await window.MusicVideoWebCodecs.render({
                            clips: clips, segs: segsPlan, song: song,
                            W: WHwc[0], H: WHwc[1], fps: 30, crf: +crf(),
                            effect: ($('mvmEffect') || {}).value || 'none',
                            transition: transitionKind(),
                            musicVol: ((parseInt($('mvmMusicVol').value, 10) || 100) / 100) * songGain,
                            musicFade: fadeOn(),
                            loopSong: songLoops(),
                            audioRemux: !!wcProbe.audioRemux,
                            signal: webcodecsAbort.signal,
                            onProgress: function (frac) {
                                window.__mvmLastActivity = wdNow();   // proof of life for the watchdog
                                var f = Math.max(0, Math.min(1, frac || 0));
                                setProgress(Math.min(98, 6 + Math.round(f * 92)),
                                    t('status_rendering', { i: Math.round(f * segsPlan.length), n: segsPlan.length }));
                            }
                        });
                        webcodecsAbort = null;
                        clearStall();
                        setProgress(100, t('status_done'));
                        showResult(totalWc);
                        if (myRun) myRun.commit();  // bill the render, not the download
                        stage('complete');
                        var wcMs = renderT0 ? Math.round(performance.now() - renderT0) : 0;
                        var wcActive = Math.round(activeMs() - renderA0);
                        saveRate('webcodecs', wcActive, totalWc);
                        ae('tool_complete', {
                            clips: clips.length, dur: Math.round(totalWc), size: resultBlob.size,
                            density: cutDensity, segs: segsPlan.length, anyKept: 0, tx: transitionKind(),
                            ms: wcMs, paused_s: Math.max(0, Math.round((wcMs - wcActive) / 1000)),
                            engine: 'webcodecs', hw: wcProbe.hardware ? 1 : 0,
                            // which H.264 made the file ('native' | 'wasm') and whether FFmpeg glued the song on
                            h264: wcProbe.h264 || '', remux: wcProbe.audioRemux ? 1 : 0
                        });
                        setBusy(false);
                        return;
                    }
                    ae('feature_use', { f: 'webcodecs_skip', reason: wcProbe.reason });
                } catch (wcErr) {
                    webcodecsAbort = null;
                    if (cancelRequested || (wcErr && wcErr.name === 'AbortError')) {
                        // User cancelled the hardware encode — honour it, no fallback.
                        clearStall();
                        handleRenderError(mark(new Error('cancelled'), 'cancelled'));
                        if (myRun) myRun.abort();
                        setBusy(false);
                        return;
                    }
                    // Any other failure → fall through to the FFmpeg pipeline. Reset
                    // the progress bar so the fallback render starts from the top.
                    ae('feature_use', { f: 'webcodecs_fallback',
                        reason: String((wcErr && wcErr.message) || 'x').slice(0, 80) });
                    setProgress(2, t('status_decoding'));
                }
            }
        }

        var ff, segNames = [], audNames = [], clipMounts = {}, anyKept = false;
        try {
            try { await window.FFmpegProcessor.init(); }
            catch (e) { throw mark(e, 'wasm'); }
            ff = window.FFmpegProcessor._getFFmpeg();

            var WH = dims(), W = WH[0], H = WH[1], C = crf();
            lastDims = WH;
            var effect = $('mvmEffect') ? $('mvmEffect').value : 'none';
            var vf = 'scale=' + W + ':' + H + ':force_original_aspect_ratio=decrease,' +
                     'pad=' + W + ':' + H + ':(ow-iw)/2:(oh-ih)/2:black,setsar=1,fps=30';
            // The incoming shot of a dissolve: the same frame geometry, no beat
            // effect — the punch-in / flash belong to the cut (as the canvas lane).
            var vfIncoming = vf + ',format=yuv420p,settb=1/30';
            if (effect === 'zoom_pulse') {
                // Punch-in on every cut (cuts land on the beat): each segment is its
                // own exec so `on` resets to 0 -> a quick zoom that settles to 1.0.
                vf += ",zoompan=z='if(eq(on,0),1.12,max(1.0,zoom-0.012))':x='iw/2-(iw/zoom/2)':y='ih/2-(ih/zoom/2)':d=1:s=" + W + "x" + H + ":fps=30";
            } else if (effect === 'flash') {
                // White burst decaying over ~130 ms from each cut (segment start;
                // cuts land on beats). fade-from-white per segment IS the flash —
                // mirrors the canvas overlay the WebCodecs engine draws.
                vf += ',fade=t=in:st=0:d=0.13:color=white';
            }
            vf += ',format=yuv420p';

            var segs = planSegments();
            anyKept = segs.some(function (s) { return s.keepAudio; });
            var MVT = window.MusicVideoTransition, txKind = transitionKind();
            var xd = segs.map(function (s, i) {
                return MVT ? MVT.dur(txKind, segs, i, function (sg) { return !!(sg.cells && sg.cells.length > 1); }) : 0;
            });
            // group segments by clip so each clip is mounted once (memory-bounded)
            var byClip = {};
            segs.forEach(function (sg, i) { (byClip[sg.clipIndex] = byClip[sg.clipIndex] || []).push(i); });

            setProgress(6, t('status_analyzing'));
            var total = 0; segs.forEach(function (s) { total += s.dur; });
            var doneCount = 0;

            /* ffmpeg.wasm never hands the heap back: every exec() grows the
               allocator, and an exec traps once its own peak no longer fits in
               what is left — so the failure depends on how much was encoded
               BEFORE it, not on the device's RAM (telemetry: it fires on 16 GB
               desktops while 4 GB phones finish).

               Measured here, 720p/veryfast, one 6 s encode after N short ones:
                 N=0  ok (7.9 s)   N=20  ok (7.1 s)   N=46  memory access out of bounds
               deleteFile()ing the finished segments first does NOT help (the
               allocator grew, not MEMFS), but FFmpegProcessor.reinit() — a
               terminate + reload off the cached core blob — resets it in ~70 ms.
               So: spend one reinit per ENCODE_BUDGET_S seconds of encoded output.
               MEMFS dies with the instance, hence finished segments are parked
               outside the heap as Blobs and written back once, for the concat. */
            var ENCODE_BUDGET_S = 15;
            var encodedSinceReset = 0;
            var carried = {};                      // name -> Blob, parked outside the wasm heap

            async function carryOut() {
                var names = segNames.concat(audNames);
                for (var q = 0; q < names.length; q++) {
                    var nm = names[q];
                    if (!nm || carried[nm]) continue;
                    // Blob the VIEW, not `d.buffer` — a view that ever arrives with a
                    // byteOffset would otherwise carry its neighbours' bytes along.
                    var d = await ff.readFile(nm);
                    carried[nm] = new Blob([d]);
                    try { await ff.deleteFile(nm); } catch (_) {}
                }
            }
            async function carryBack() {
                var names = Object.keys(carried);
                for (var q2 = 0; q2 < names.length; q2++) {
                    await ff.writeFile(names[q2], new Uint8Array(await carried[names[q2]].arrayBuffer()));
                }
            }
            // Fresh heap. Mounts belong to the instance that created them, so the
            // caller re-mounts whatever it still needs.
            async function recycle() {
                await carryOut();
                await window.FFmpegProcessor.reinit();
                ff = window.FFmpegProcessor._getFFmpeg();
                encodedSinceReset = 0;
            }

            for (var ci in byClip) {
                var clip = clips[ci];
                var mnt = await window.FFmpegProcessor.mountInput(clip.file);
                clipMounts[ci] = mnt;
                try {
                    for (var k = 0; k < byClip[ci].length; k++) {
                        var sIdx = byClip[ci][k];
                        var sg = segs[sIdx];
                        // Give this encode a heap it can fit in. A segment longer
                        // than the whole budget gets a fresh instance to itself.
                        if (encodedSinceReset > 0 && encodedSinceReset + sg.dur > ENCODE_BUDGET_S) {
                            try { await mnt.dispose(); } catch (_) {}
                            await recycle();
                            mnt = await window.FFmpegProcessor.mountInput(clip.file);
                            clipMounts[ci] = mnt;
                        }
                        var name = 'seg_' + sIdx + '.mp4';
                        // A still has no timeline to seek into: it is looped
                        // for the segment's length instead.
                        var args = (clip.isImage
                            ? ['-loop', '1', '-i', mnt.path, '-t', String(sg.dur)]
                            : ['-ss', String(sg.start), '-i', mnt.path, '-t', String(sg.dur)]).concat([
                            '-vf', vf, '-an', '-fps_mode', 'cfr', '-r', '30',
                            '-c:v', 'libx264', '-preset', 'veryfast', '-crf', C,
                            '-pix_fmt', 'yuv420p', '-y', name]);
                        /* «Наплыв» (MusicVideoTransition): the tail of this
                           segment blends into the next one, ending on the cut —
                           the next clip enters as a second input, the `dx`
                           seconds that lead up to its own start (its first frame
                           held where it has none), without the beat effect.
                           xfade's output = offset + the second input's length =
                           this segment's length: the montage keeps its timing. */
                        var dx = xd[sIdx], nMnt = null;
                        if (dx > 0) {
                            var nsg = segs[sIdx + 1], nclip = clips[nsg.clipIndex];
                            var nPath = mnt.path;
                            if (String(nsg.clipIndex) !== String(ci)) {
                                nMnt = await window.FFmpegProcessor.mountInput(nclip.file);
                                nPath = nMnt.path;
                            }
                            var lead = Math.min(dx, nsg.start || 0), hold = dx - lead;
                            var inA = clip.isImage
                                ? ['-loop', '1', '-t', String(sg.dur), '-i', mnt.path]
                                : ['-ss', String(sg.start), '-t', String(sg.dur), '-i', mnt.path];
                            var inB = nclip.isImage
                                ? ['-loop', '1', '-t', dx.toFixed(3), '-i', nPath]
                                : ['-ss', Math.max(0, (nsg.start || 0) - lead).toFixed(3), '-t', (lead > 0.034 ? lead : 0.1).toFixed(3), '-i', nPath];
                            var fc = '[0:v]' + vf + ',settb=1/30[a];' +
                                '[1:v]' + vfIncoming + (hold > 0.001 ? ',tpad=start_duration=' + hold.toFixed(3) + ':start_mode=clone' : '') +
                                ',trim=duration=' + dx.toFixed(3) + ',setpts=PTS-STARTPTS[b];' +
                                '[a][b]xfade=transition=fade:duration=' + dx.toFixed(3) + ':offset=' + Math.max(0, sg.dur - dx).toFixed(3) + ',format=yuv420p[v]';
                            args = inA.concat(inB).concat([
                                '-filter_complex', fc, '-map', '[v]', '-an', '-fps_mode', 'cfr', '-r', '30',
                                '-c:v', 'libx264', '-preset', 'veryfast', '-crf', C,
                                '-pix_fmt', 'yuv420p', '-y', name]);
                        }
                        window._ffLogs = [];
                        var code;
                        try { code = await ff.exec(args); }
                        finally { if (nMnt) { try { await nMnt.dispose(); } catch (_) {} } }
                        // A nonzero exit is ffmpeg REPORTING a failure (bad stream,
                        // unsupported pixel format...). Real heap exhaustion THROWS
                        // a RuntimeError and is classified in the catch. Calling
                        // every nonzero exit 'oom' — as this did until 2026-07-10 —
                        // told users to cut clips and resolution over a decode error.
                        if (code !== 0) throw mark(new Error('seg ' + sIdx + ' exit ' + code + ': ' + ffTail()), 'process');
                        encodedSinceReset += sg.dur;
                        segNames[sIdx] = name;

                        // Tier-2 (per-clip keep-audio): extract this segment's audio
                        // slice for the ducking timeline. If the clip has no audio
                        // track the exec fails -> silence-filled below.
                        if (anyKept && sg.keepAudio) {
                            var an = 'aud_' + sIdx + '.m4a';
                            // per-clip volume: scale THIS clip's sound before it
                            // enters the ducking timeline (0 = kept but silent)
                            var cg = (clip.gain == null ? 1 : clip.gain);
                            var extAf = (cg !== 1 ? 'volume=' + cg.toFixed(2) + ',' : '') + 'aresample=44100';
                            window._ffLogs = [];
                            var ac = await ff.exec(['-ss', String(sg.start), '-i', mnt.path, '-t', String(sg.dur),
                                '-vn', '-af', extAf, '-ac', '1', '-c:a', 'aac', '-b:a', '160k', '-y', an]);
                            if (ac === 0) audNames[sIdx] = an;
                            encodedSinceReset += sg.dur;
                        }
                        doneCount++;
                        setProgress(6 + Math.round(doneCount / segs.length * 74),
                            t('status_rendering', { i: doneCount, n: segs.length }));
                        if (cancelRequested) throw mark(new Error('cancelled'), 'cancelled');
                    }
                } finally { try { await mnt.dispose(); } catch (_) {} clipMounts[ci] = null; }
            }

            // Every heavy encode is done. Hand the silence-fill / concat / mux a
            // fresh instance with all the segments back in MEMFS, so the muxer
            // never inherits the allocator the montage grew. `clipaudio.m4a` is
            // both produced and consumed after this point, so it never travels.
            if (cancelRequested) throw mark(new Error('cancelled'), 'cancelled');
            await recycle();
            await carryBack();

            // Tier-2: complete the aligned clip-audio timeline with silence where a
            // segment was muted (or had no audio), then concat into ONE track that
            // lines up frame-for-frame with the video montage.
            if (anyKept) {
                for (var si = 0; si < segs.length; si++) {
                    if (audNames[si]) continue;
                    var sn = 'aud_' + si + '.m4a';
                    window._ffLogs = [];
                    var scode = await ff.exec(['-f', 'lavfi', '-i', 'anullsrc=channel_layout=mono:sample_rate=44100',
                        '-t', String(segs[si].dur), '-c:a', 'aac', '-b:a', '160k', '-y', sn]);
                    if (scode !== 0) throw mark(new Error('silence ' + si + ': ' + ffTail()), 'process');
                    audNames[si] = sn;
                }
                var alist = '';
                for (var aj = 0; aj < audNames.length; aj++) if (audNames[aj]) alist += "file '" + audNames[aj] + "'\n";
                await ff.writeFile('alist.txt', new TextEncoder().encode(alist));
                window._ffLogs = [];
                var atc = await ff.exec(['-f', 'concat', '-safe', '0', '-i', 'alist.txt', '-c', 'copy', '-y', 'clipaudio.m4a']);
                if (atc !== 0) throw mark(new Error('audio timeline concat: ' + ffTail()), 'process');
            }

            // concat video segments + mux song
            setProgress(84, t('status_muxing'));
            var list = '';
            for (var j = 0; j < segNames.length; j++) if (segNames[j]) list += "file '" + segNames[j] + "'\n";
            await ff.writeFile('list.txt', new TextEncoder().encode(list));

            var songMnt = await window.FFmpegProcessor.mountInput(song.file);
            var outName = 'music-video.mp4';
            try {
                var vol = ((parseInt($('mvmMusicVol').value, 10) || 100) / 100) * songGain;
                // Sequential mode plays clips in full, so the video can be longer
                // than the song. Loop toggle ON (default) -> -stream_loop fills the
                // tail and -shortest ends at the video. Loop OFF -> the song plays
                // once and the tail is PADDED with silence (`apad` + explicit -t):
                // keeping -shortest there would cut the VIDEO at the song's end.
                var wantLoop = songLoops();
                var loopSong = wantLoop ? ['-stream_loop', '-1'] : [];
                var padTail = !wantLoop && targetSec() > song.duration + 0.25;
                var lenArgs = padTail ? ['-t', String(total.toFixed(3))] : ['-shortest'];
                var padChain = padTail ? ',apad' : '';
                // Optional fade toggle: 0.25 s in / 1.2 s out against the montage
                // length (clamped for tiny montages) — the same envelope the
                // WebCodecs engine applies via a gain ramp.
                var fadeChain = '';
                if (fadeOn() && total > 1) {
                    var fIn = Math.min(0.25, total / 4), fOut = Math.min(1.2, total / 3);
                    fadeChain = ',afade=t=in:st=0:d=' + fIn.toFixed(2) +
                        ',afade=t=out:st=' + Math.max(fIn, total - fOut).toFixed(2) + ':d=' + fOut.toFixed(2);
                }
                var muxArgs;
                if (anyKept) {
                    // Per-clip ducking: sidechain-compress the song keyed by the kept
                    // clip-audio timeline (input 1) — song dips only where a clip's own
                    // sound is present — then mix the kept audio back on top. The
                    // clip_duck toggle (default ON) drops the sidechain stage: music
                    // holds its level under the clips' sound.
                    var fc;
                    if (duckOn()) {
                        fc = '[2:a]volume=' + vol.toFixed(2) + ',aresample=44100[m];' +
                            '[m][1:a]sidechaincompress=threshold=0.04:ratio=10:attack=20:release=300[d];' +
                            '[d][1:a]amix=inputs=2:duration=first:normalize=0[mx];' +
                            '[mx]alimiter=limit=0.94:attack=5:release=50:asc=1:level=disabled' + fadeChain + padChain + '[fa]';
                    } else {
                        fc = '[2:a]volume=' + vol.toFixed(2) + ',aresample=44100[m];' +
                            '[m][1:a]amix=inputs=2:duration=first:normalize=0[mx];' +
                            '[mx]alimiter=limit=0.94:attack=5:release=50:asc=1:level=disabled' + fadeChain + padChain + '[fa]';
                    }
                    muxArgs = ['-f', 'concat', '-safe', '0', '-i', 'list.txt', '-i', 'clipaudio.m4a']
                        .concat(loopSong).concat(['-i', songMnt.path,
                            '-filter_complex', fc, '-map', '0:v:0', '-map', '[fa]'])
                        .concat(lenArgs).concat(['-fps_mode', 'cfr',
                            '-c:v', 'copy', '-c:a', 'aac', '-b:a', '192k', '-ar', '44100', '-movflags', '+faststart', '-y', outName]);
                } else {
                    var af = 'volume=' + vol.toFixed(2) + ',alimiter=limit=0.94:attack=5:release=50:asc=1:level=disabled' + fadeChain + padChain;
                    muxArgs = ['-f', 'concat', '-safe', '0', '-i', 'list.txt']
                        .concat(loopSong).concat(['-i', songMnt.path,
                            '-map', '0:v:0', '-map', '1:a:0'])
                        .concat(lenArgs).concat(['-fps_mode', 'cfr',
                            '-af', af, '-c:v', 'copy', '-c:a', 'aac', '-b:a', '192k', '-ar', '44100',
                            '-movflags', '+faststart', '-y', outName]);
                }
                window._ffLogs = [];
                var mc = await ff.exec(muxArgs);
                if (mc !== 0) throw mark(new Error('mux exit ' + mc + ': ' + ffTail()), 'process');
                var data = await ff.readFile(outName);
                resultBlob = new Blob([data.buffer || data], { type: 'video/mp4' });
            } finally {
                try { await songMnt.dispose(); } catch (_) {}
                for (var n2 = 0; n2 < segNames.length; n2++) if (segNames[n2]) { try { await ff.deleteFile(segNames[n2]); } catch (_) {} }
                for (var na = 0; na < audNames.length; na++) if (audNames[na]) { try { await ff.deleteFile(audNames[na]); } catch (_) {} }
                try { await ff.deleteFile('list.txt'); } catch (_) {}
                try { await ff.deleteFile('alist.txt'); } catch (_) {}
                try { await ff.deleteFile('clipaudio.m4a'); } catch (_) {}
                try { await ff.deleteFile(outName); } catch (_) {}
            }

            setProgress(100, t('status_done'));
            showResult(total);
            if (myRun) myRun.commit();   // music video rendered → bill one run (not the download)
            stage('complete');
            var ffMs = renderT0 ? Math.round(performance.now() - renderT0) : 0;
            var ffActive = Math.round(activeMs() - renderA0);
            saveRate('ffmpeg', ffActive, total);
            ae('tool_complete', {
                clips: clips.length, dur: Math.round(total), size: resultBlob.size, density: cutDensity,
                segs: segs.length, anyKept: anyKept ? 1 : 0, tx: txKind,
                ms: ffMs, paused_s: Math.max(0, Math.round((ffMs - ffActive) / 1000)),
                engine: 'ffmpeg'
            });
        } catch (e) {
            handleRenderError(e);
        } finally {
            clearStall();
            if (myRun) myRun.abort();   // idempotent after commit; cancel / failure → never bills
            setBusy(false);
        }
    }

    function mark(e, cls) { try { e._mvmClass = cls; } catch (_) {} return e; }
    function ffTail() { try { return (window._ffLogs || []).slice(-6).join(' | '); } catch (_) { return ''; } }

    function handleRenderError(e) {
        var msg = String((e && e.message) || e || '');
        var cls = e && e._mvmClass;
        $('mvmProg').classList.add('mvm-hidden');
        // Watchdog verdict wins over every classification: the abort it forced
        // surfaces here as 'cancelled' (WC) or a terminated-worker error
        // (FFmpeg reinit), and both must read as «обработка зависла», not as a
        // user cancel or a generic failure.
        if (renderStalled) {
            showErr(t('err_stalled'));
            stage('error');
            ae('tool_error', { error_type: 'stalled_surfaced', err_msg: msg.slice(0, 120), clips: clips.length });
            return;
        }
        if (cls === 'cancelled') { ae('feature_use', { f: 'render_cancelled' }); return; }  // user aborted — no error surface
        // NOT `Aborted`: ffmpeg.wasm ends every exec by unwinding through
        // Emscripten's exit path, so a healthy render prints "Aborted()" once per
        // segment (30 times on a 20 s song, measured). Matching it here turned any
        // failure whose log tail happened to include that line into a bogus
        // out-of-memory verdict.
        // x264 inside the wasm heap says it its own way: «x264 [error]: malloc of size N failed»
        // (prod, 30 d: 6 renders told «не удалось собрать клип» instead of «не хватило памяти»)
        var oom = /out of bounds|cannot enlarge memory|OOM|allocation failed|out of memory|malloc of size \d+ failed/i.test(msg);
        if (cls === 'wasm') showErr(t('err_wasm'));
        else if (cls === 'oom' || oom) showErr(t('err_oom'));
        else showErr(t('err_processing'));
        stage('error');
        ae('tool_error', { error_type: cls || 'render', err_msg: msg.slice(0, 240), oom: oom ? 1 : 0, clips: clips.length });
    }

    function showResult(total, opts) {
        opts = opts || {};
        $('mvmProg').classList.add('mvm-hidden');   // remove progress + Cancel once the clip is ready
        var box = $('mvmResult');
        box.classList.remove('mvm-hidden');
        var v = $('mvmResultVideo');
        if (v.src) try { URL.revokeObjectURL(v.src); } catch (_) {}
        v.src = URL.createObjectURL(resultBlob);
        var meta = t('result_duration') + ': ' + fmtTime(total) + ' · ' + t('result_size') + ': ' + fmtSize(resultBlob.size);
        if (lastDims) meta += ' · ' + t('result_resolution') + ': ' + lastDims[0] + '×' + lastDims[1];
        $('mvmResultMeta').textContent = meta;
        // fresh result → the download button is back to its first-run label
        downloadedOnce = false;
        var lbl = $('mvmDownloadLabel');
        if (lbl && lbl.dataset.first) lbl.textContent = lbl.dataset.first;
        /* Bring the ACTION into view, not the top of the preview. Measured
           2026-08-23 on a real render with nobody touching the page: scrolling
           the <video> into view left «Скачать» 122 px below the fold on a
           360×800 phone and 378 px below it on 1280×900 — i.e. the button that
           finishes the job was off-screen on BOTH. 30 d telemetry agrees this
           is not a corner case: 147 of 343 finished clips (43 %) were never
           downloaded, and the share is the same for people who stayed on the
           tab as for people who left, which no "they walked away" story
           explains. block:'nearest' keeps the global no-auto-scroll rule: it
           moves the page only when the target is off-screen. */
        var bringUp = function () { try { ($('mvmDownload') || v).scrollIntoView({ block: 'nearest' }); } catch (_) {} };
        bringUp();
        /* Once more when the <video> reports its size: the poster-less element
           is short until then, so the first scroll aims at a button that the
           metadata reflow pushes back down — measured 13 px of it still cut off
           on a 360×800 phone. 'nearest' is a no-op when the button is already
           fully visible, so this cannot become a second jump. */
        v.addEventListener('loadedmetadata', bringUp, { once: true });
        if (opts.restored) return;
        // Insurance first (survives a closed tab), delivery second, recall third.
        resultDelivered = false;
        persistResult(total);
        if (autoDownloadOn()) download(true);
        recallSignal();
    }

    function download(auto) {
        if (!resultBlob) return;
        var name = 'music-video-' + Date.now() + '.mp4';
        var r;
        if (window.__downloadFile) r = window.__downloadFile(resultBlob, name);
        else { var a = document.createElement('a'); a.href = URL.createObjectURL(resultBlob); a.download = name; a.click(); }
        if (!downloadedOnce) {
            downloadedOnce = true;
            var lbl = $('mvmDownloadLabel');
            if (lbl && T.download_again) {
                if (!lbl.dataset.first) lbl.dataset.first = lbl.textContent;
                lbl.textContent = T.download_again;
            }
        }
        /* `auto` marks the ones the page took on itself. Without the flag the
           download rate would climb by construction and prove nothing about
           whether the file reached anybody — the number this whole change is
           trying to move. A promise the primitive REFUSES (returns false /
           rejects) is recorded too: on iOS a gesture-less save can be dropped
           silently, and the saved-clip banner is what covers that case. */
        markResultDelivered();
        ae('feature_use', { f: 'download', auto: auto ? 1 : 0 });
        if (auto && r && typeof r.then === 'function') {
            r.then(function (okDl) {
                if (okDl === false) ae('feature_use', { f: 'autodownload_refused' });
            }, function () { ae('feature_use', { f: 'autodownload_refused' }); });
        }
    }

    function resetProject() {
        song = null; clips = []; resultBlob = null;
        ['mvmSongBar', 'mvmClipsWrap', 'mvmSettingsWrap', 'mvmExportWrap', 'mvmResult', 'mvmProg'].forEach(function (id) { $(id).classList.add('mvm-hidden'); });
        $('mvmSongZone').classList.remove('mvm-hidden');
        $('mvmClipsZone').classList.remove('mvm-hidden');
        if (preview) { preview.pause(); preview.setSong(null); preview.setClips([]); preview.setPlan([]); }
        var _tk2 = $('mvmTrack'); if (_tk2) _tk2.classList.add('mvm-hidden');
        var _sc = $('mvmScreen'); if (_sc) _sc.classList.remove('has-media');
        renderClips();
        try { localStorage.removeItem(RESTORE_KEY); } catch (_) {}
        // «Новый проект» is the one action that says the old clip is done with.
        try { localStorage.removeItem(RESULT_KEY); } catch (_) {}
        var sv = $('mvmSaved'); if (sv) sv.classList.add('mvm-hidden');
    }

    /* ─────────── project restore (crash-reload survivability) ───────────
       02.08 (iPhone/YaBrowser, paying user): one pick of 634 MB of MOVs
       killed the WKWebView tab mid-ingest; the silent reload came back
       BLANK — song and clips gone, no explanation («Ну что за фигня»), and
       she re-picked everything by hand 90 s later. The bytes had already
       survived: FileBytes.spill had streamed both big MOVs to OPFS before
       the crash. What died was the map back to them. So: every project file
       is spilled (streamed + serialized — no heap cost) and a manifest
       (name/size/type/mtime + OPFS spill name) persists in localStorage.
       The next load, if the manifest is fresh and the bytes are still on
       disk, offers a one-tap restore that re-injects the files through the
       NORMAL uploader pipeline (probe → thumbs → BPM) — no parallel code
       path. Deliberately NOT a beforeunload dialog: the 08-01 measurement
       (reference_work_loss_vs_settings_persistence) forbids spreading those. */
    var RESTORE_KEY = 'mvm_project_v1';
    var RESTORE_TTL_MS = 45 * 60 * 1000;

    function restoreEntry(file) {
        var sp = (window.FileBytes && typeof window.FileBytes.spillInfo === 'function')
            ? window.FileBytes.spillInfo(file) : null;
        return { n: file.name, s: file.size || 0, t: file.type || '', m: file.lastModified || 0, sp: sp ? sp.name : null };
    }

    var persistBusy = false, persistAgain = false;
    function persistProject() {
        if (!window.FileBytes || typeof window.FileBytes.spill !== 'function'
            || typeof window.FileBytes.spillInfo !== 'function') return;
        if (persistBusy) { persistAgain = true; return; }
        persistBusy = true;
        var files = [];
        if (song) files.push(song.file);
        clips.forEach(function (c) { files.push(c.file); });
        if (!files.length) {
            try { localStorage.removeItem(RESTORE_KEY); } catch (_) {}
            persistBusy = false;
            if (persistAgain) { persistAgain = false; persistProject(); }
            return;
        }
        Promise.all(files.map(function (f) {
            return window.FileBytes.spill(f).then(null, function () { return null; });
        })).then(function () {
            try {
                var man = {
                    ts: Date.now(),
                    song: song ? restoreEntry(song.file) : null,
                    clips: clips.map(function (c) { return restoreEntry(c.file); }),
                };
                localStorage.setItem(RESTORE_KEY, JSON.stringify(man));
            } catch (_) {}
            persistBusy = false;
            if (persistAgain) { persistAgain = false; persistProject(); }
        });
    }

    async function offerRestore() {
        var raw = null;
        try { raw = localStorage.getItem(RESTORE_KEY); } catch (_) { return; }
        if (!raw) return;
        var man = null;
        try { man = JSON.parse(raw); } catch (_) {}
        var clear = function () { try { localStorage.removeItem(RESTORE_KEY); } catch (_) {} };
        if (!man || !man.ts || (Date.now() - man.ts) > RESTORE_TTL_MS) { clear(); return; }
        if (!window.FileBytes || typeof window.FileBytes.readSpilled !== 'function') return;
        var songFile = null, clipFiles = [];
        if (man.song && man.song.sp) {
            songFile = await window.FileBytes.readSpilled(man.song.sp,
                { name: man.song.n, size: man.song.s, type: man.song.t, lastModified: man.song.m });
        }
        var list = man.clips || [];
        for (var i = 0; i < list.length; i++) {
            if (!list[i].sp) continue;
            var f = await window.FileBytes.readSpilled(list[i].sp,
                { name: list[i].n, size: list[i].s, type: list[i].t, lastModified: list[i].m });
            if (f) clipFiles.push(f);
        }
        if (!songFile && !clipFiles.length) { clear(); return; }
        var bar = $('mvmRestore'), txt = $('mvmRestoreText');
        if (!bar || !txt) return;
        var parts = [];
        if (songFile) parts.push('«' + (songFile.name.length > 28 ? songFile.name.slice(0, 27) + '…' : songFile.name) + '»');
        if (clipFiles.length) parts.push(t('restore_clips', { n: clipFiles.length }));
        txt.textContent = t('restore_found') + ' ' + parts.join(' + ');
        bar.classList.remove('mvm-hidden');
        ae('feature_use', { f: 'project_restore_offered', clips: clipFiles.length, song: songFile ? 1 : 0 });
        $('mvmRestoreGo').addEventListener('click', function () {
            bar.classList.add('mvm-hidden');
            ae('feature_use', { f: 'project_restore_used', clips: clipFiles.length, song: songFile ? 1 : 0 });
            var se = $('mvmSong'), ce = $('mvmClips');
            if (songFile && se && se.__uploaderInjectFile) se.__uploaderInjectFile(songFile, 'crash_restore');
            if (clipFiles.length && ce && ce.__uploaderInjectFiles) ce.__uploaderInjectFiles(clipFiles, 'crash_restore');
        }, { once: true });
        $('mvmRestoreX').addEventListener('click', function () {
            bar.classList.add('mvm-hidden');
            clear();
            ae('feature_use', { f: 'project_restore_dismissed' });
        }, { once: true });
    }

    /* ────── the finished clip survives the tab (2026-08-23) ──────
       Bug-report «Где можно найти, готов ли клип? Не могу найти» (Тигрица,
       23.08). She was looking for a place that did not exist: the montage is
       browser-side, so the finished MP4 lived ONLY as a Blob in the tab's
       memory — close it, reload it, let Android discard it, and the clip was
       gone with the daily run already spent (the cap commits when the result
       is SHOWN, not when it is saved).

       Measured, 30 days: 455 sessions started a render, 343 got a clip, 196
       downloaded one. 147 finished clips (43 %) reached nobody. Leaving the
       tab is NOT the explanation — the download share is the same for people
       who stayed (56 %) and people who left (57 %) — so this ships three
       independent layers instead of one guess:
         1. the bytes go to OPFS (same spill tier the project restore uses),
            so a closed tab is survivable and the offer comes back on return;
         2. the page saves the file itself when the setting is on;
         3. the page says the clip is ready when nobody is looking at the tab.

       OPFS entries are swept at 24 h by FileBufferCache, so the manifest
       honours the same horizon rather than promising a file that is gone. */
    var RESULT_KEY = 'mvm_result_v1';
    var RESULT_TTL_MS = 24 * 60 * 60 * 1000;

    function readResultManifest() {
        try {
            var man = JSON.parse(localStorage.getItem(RESULT_KEY) || 'null');
            if (!man || !man.ts || !man.sp) return null;
            if (Date.now() - man.ts > RESULT_TTL_MS) { localStorage.removeItem(RESULT_KEY); return null; }
            return man;
        } catch (_) { return null; }
    }

    /* Fire-and-forget: the clip is already on screen, so this is pure
       insurance and must never delay or break the visible result. Average
       output is 63 MB (30 d telemetry; max 370 MB) and OPFS streams at
       ~14 MB/s here, so this is seconds of background IO with no heap cost —
       file.stream().pipeTo() never materialises the bytes twice. */
    function persistResult(total) {
        if (!resultBlob) return;
        /* No spill tier at all. This is a REAL production state, not a
           theoretical one: when file-buffer-cache.js is blocked (ad-blocker,
           CSP, CDN double-miss) the layout installs a minimal FileBytes shim
           that has read/prime and no spill. Saying nothing here would leave the
           tab as the only copy while the page looked exactly as if it had been
           saved — the failure shape this whole change exists to remove.
           Deferred a tick so the auto-save (which runs right after this call)
           has already reported: a clip the page saved needs no warning. */
        if (!window.FileBytes || typeof window.FileBytes.spill !== 'function') {
            ae('feature_use', { f: 'result_persist_unavailable', shim: window.FileBytes && window.FileBytes.version === 'shim' ? 1 : 0 });
            setTimeout(warnTabOnly, 0);
            return;
        }
        // Snapshot everything the manifest needs NOW: the write finishes seconds
        // later, and by then a second render may already own `lastDims`.
        var blob = resultBlob, t0 = Date.now();
        var dims0 = lastDims ? [lastDims[0], lastDims[1]] : null;
        window.FileBytes.spill(blob).then(function (okSpill) {
            var info = okSpill && window.FileBytes.spillInfo ? window.FileBytes.spillInfo(blob) : null;
            if (!info || !info.name) {
                ae('feature_use', { f: 'result_persist_failed', szMB: Math.round(blob.size / 1048576) });
                warnTabOnly();
                return;
            }
            try {
                /* `dl` is read at WRITE time, not at call time: the spill takes
                   seconds and the auto-save fires immediately, so a hardcoded 0
                   here would resurrect an already-delivered clip as an unread
                   offer on the next load. */
                localStorage.setItem(RESULT_KEY, JSON.stringify({
                    ts: Date.now(), sp: info.name, size: blob.size, dur: Math.round(total || 0),
                    w: dims0 ? dims0[0] : 0, h: dims0 ? dims0[1] : 0,
                    dl: resultDelivered ? 1 : 0,
                }));
            } catch (_) { return; }
            ae('feature_use', { f: 'result_persisted', szMB: Math.round(blob.size / 1048576), ms: Date.now() - t0 });
        }, function () {
            ae('feature_use', { f: 'result_persist_failed', szMB: Math.round(blob.size / 1048576) });
            warnTabOnly();
        });
    }

    /* Both safety nets are off (no disk copy AND the page did not save it):
       say so once, plainly, instead of letting the tab quietly be the only
       copy. Silence here is precisely what produced «Не могу найти» — the
       clip was gone and nothing had ever suggested it could be.
       Quota exhaustion and Safari < 17 (no createWritable on OPFS) are the two
       measured ways to land here. */
    function warnTabOnly() {
        if (resultDelivered) return;
        var box = $('mvmResultMeta');
        if (!box || box.dataset.tabOnly) return;
        box.dataset.tabOnly = '1';
        var line = document.createElement('div');
        line.className = 'mvm-tabonly';
        line.textContent = t('keep_tab_hint');
        box.appendChild(line);
        ae('feature_use', { f: 'tab_only_warned' });
    }

    /* A delivered clip stops asking to be delivered. The row stays on disk —
       a browser download can still be lost — but the banner is for the person
       who never got the file, not for the one who did. */
    var resultDelivered = false;
    function markResultDelivered() {
        resultDelivered = true;                          // persist may still be in flight
        var man = readResultManifest();
        if (!man) return;
        man.dl = 1;
        try { localStorage.setItem(RESULT_KEY, JSON.stringify(man)); } catch (_) {}
    }

    async function offerSavedResult() {
        var man = readResultManifest();
        if (!man || man.dl || resultDelivered) return;
        if (!window.FileBytes || typeof window.FileBytes.readSpilled !== 'function') return;
        var f = await window.FileBytes.readSpilled(man.sp, { name: 'music-video.mp4', size: man.size, type: 'video/mp4' });
        if (!f) { try { localStorage.removeItem(RESULT_KEY); } catch (_) {} return; }
        var bar = $('mvmSaved'), txt = $('mvmSavedText');
        if (!bar || !txt) return;
        var parts = [];
        if (man.dur) parts.push(fmtTime(man.dur));
        parts.push(fmtSize(man.size));
        if (man.w && man.h) parts.push(man.w + '×' + man.h);
        txt.textContent = t('saved_found') + ' ' + parts.join(' · ');
        bar.classList.remove('mvm-hidden');
        ae('feature_use', { f: 'saved_result_offered', szMB: Math.round(man.size / 1048576), ageMin: Math.round((Date.now() - man.ts) / 60000) });
        $('mvmSavedGo').addEventListener('click', function () {
            bar.classList.add('mvm-hidden');
            resultBlob = f;
            lastDims = (man.w && man.h) ? [man.w, man.h] : null;
            ae('feature_use', { f: 'saved_result_used', szMB: Math.round(man.size / 1048576) });
            showResult(man.dur || 0, { restored: true });
            download();
        }, { once: true });
        $('mvmSavedX').addEventListener('click', function () {
            bar.classList.add('mvm-hidden');
            try { localStorage.removeItem(RESULT_KEY); } catch (_) {}
            ae('feature_use', { f: 'saved_result_dismissed' });
        }, { once: true });
    }

    function autoDownloadOn() {
        var el = $('mvmAutoDl');
        return !!(el && el.checked);
    }

    /* Nobody is looking at the tab for most of a render — 225 of the 455
       sessions above left it, and the FFmpeg lane averages 12–21 minutes. The
       title is the free, permissionless half of the answer; the notification
       is the half that reaches a phone, and it is only ever armed by an
       explicit tick (permission prompts belong to a gesture, not to a page
       load). */
    var titleBefore = null;
    function restoreTitle() {
        if (titleBefore === null) return;
        document.title = titleBefore;
        titleBefore = null;
        document.removeEventListener('visibilitychange', onVisRestore);
        window.removeEventListener('focus', restoreTitle);
    }
    function onVisRestore() { if (!document.hidden) restoreTitle(); }
    function recallSignal() {
        if (!document.hidden) return;
        try {
            if (titleBefore === null) titleBefore = document.title;
            document.title = '✅ ' + t('result_ready');
            document.addEventListener('visibilitychange', onVisRestore);
            window.addEventListener('focus', restoreTitle);
        } catch (_) {}
        try {
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                var n = new Notification(t('result_ready'), { body: t('notify_body'), tag: 'mvm-done' });
                n.onclick = function () { try { window.focus(); } catch (_) {} n.close(); };
                ae('feature_use', { f: 'notify_sent' });
            }
        } catch (_) {}
        ae('feature_use', { f: 'done_while_hidden' });
    }

    /* ───────────────────────── wiring ───────────────────────── */

    function onUploaderEvent(e, handler) {
        var d = e.detail || {};
        if (d.files && d.files.length) handler(Array.prototype.slice.call(d.files));
        else if (d.file) handler([d.file]);
    }

    /* A chip row that drives a hidden <select>. The select stays the single
       source of truth — every reader (dims(), crf(), the quality gate, the
       persisted setting, the `change` listeners) keeps working untouched — and
       the chips are what a person actually sees. Four visible options beat a
       closed dropdown on a phone, and cost the same pixels. */
    // A collage keeps hard cuts (a blend of two grids would need up to eight live
    // decoders): with «Наплыв» picked, the row says so instead of failing silently.
    function syncTransitionHint() {
        var h = $('mvmTransitionHint');
        if (h) h.classList.toggle('mvm-hidden', !(collage && transitionKind() === 'dissolve'));
    }

    function bindChips(segId, selId, onPick) {
        var seg = $(segId), sel = $(selId);
        if (!seg || !sel) return;
        function paint() {
            seg.querySelectorAll('button[data-v]').forEach(function (b) {
                var on = b.dataset.v === sel.value;
                b.classList.toggle('on', on);
                b.setAttribute('aria-pressed', on ? 'true' : 'false');
            });
        }
        seg.addEventListener('click', function (e) {
            var b = e.target.closest('button[data-v]'); if (!b) return;
            if (sel.value === b.dataset.v) return;
            sel.value = b.dataset.v;
            paint();
            sel.dispatchEvent(new Event('change', { bubbles: true }));
            if (onPick) onPick(b.dataset.v);
        });
        sel.addEventListener('change', paint);
        paint();
        return paint;
    }

    /* `:has(input:checked)` styles the pill in every current browser; the class
       is the fallback for the ones where it does not, and it costs one line. */
    /* «Зациклить песню» is only a question when the montage OUTLASTS the song.
       It used to be shown for the whole of «по очереди целиком» and hidden for
       the whole of «под бит», which is neither: a beat montage set to fit the
       footage can easily outlast the track, and a sequential one under a long
       song never reaches the end of it. Asking only where the answer changes
       the file is the difference between a setting and a decoration. */
    function syncLoopRow() {
        var row = $('mvmLoopRow'); if (!row) return;
        var over = !!song && targetSec() > song.duration + 0.25;
        row.classList.toggle('mvm-hidden', !over);
    }

    function bindPill(pillId, boxId, onChange) {
        var pill = $(pillId), box = $(boxId);
        if (!pill || !box) return;
        var sync = function () { pill.classList.toggle('on', !!box.checked); };
        box.addEventListener('change', function () { sync(); if (onChange) onChange(); });
        sync();
        return sync;
    }

    function init() {
        var songEl = $('mvmSong'), clipsEl = $('mvmClips');
        if (!songEl) return;

        // Chip rows ↔ hidden selects (see bindChips). Aspect additionally
        // reshapes the preview screen, so the frame you judge is the frame you
        // get.
        bindChips('mvmAspectSeg', 'mvmAspect', function () { applyScreenAspect(); });
        bindChips('mvmQualitySeg', 'mvmQuality');
        // The effect is the person's: «Нет» until they pick one (or a pick
        // remembered from an earlier visit). One photo under a song is a still
        // for the whole song in 86 % of such renders — a «Пульс-зум» switched on
        // by itself (22.10–23.10.2026) moved photos nobody asked to move.
        // PhotoUnderASongStaysStillUnlessAskedTest, docs/why/photo-motion-default.md.
        bindChips('mvmEffectSeg', 'mvmEffect');
        // «Переход»: «Резкая склейка» unless the person picks «Наплыв» — a default
        // that changes the RESULT is the field's choice, not one request's
        // (docs/why/photo-motion-default.md). The preview plays the same rule.
        bindChips('mvmTransitionSeg', 'mvmTransition', function (v) {
            ae('feature_use', { f: 'transition_change', v: v });
        });
        var txSel = $('mvmTransition');
        if (txSel) txSel.addEventListener('change', function () {
            if (preview && preview.setTransition) preview.setTransition(transitionKind());
            syncTransitionHint();
        });
        var vizDoor = $('mvmVizDoorLink');
        if (vizDoor) vizDoor.addEventListener('click', function () { ae('feature_use', { f: 'viz_door_click', v: clips.length }); });
        ['mvmFadePill|mvmFade', 'mvmNormPill|mvmNorm', 'mvmDuckPill|mvmDuck',
         'mvmLoopPill|mvmLoop', 'mvmAutoDlPill|mvmAutoDl', 'mvmNotifyPill|mvmNotify'
        ].forEach(function (pair) { var p = pair.split('|'); bindPill(p[0], p[1]); });

        if (window.MusicVideoPreview) {
            preview = window.MusicVideoPreview.create({
                canvas: $('mvmPreview'), track: $('mvmTrack'), head: $('mvmHead'),
                playBtn: $('mvmPlay'), playIcon: $('mvmPlayIcon'),
                tcNow: $('mvmTcNow'), tcAll: $('mvmTcAll'), cutInfo: $('mvmCutInfo'),
                t: T,
                onSeek: function () { ae('feature_use', { f: 'preview_seek' }); }
            });
        }

        // ── Duration contract ──────────────────────────────────────────────
        var fitEl = $('mvmFit');
        if (fitEl) fitEl.addEventListener('click', function (e) {
            var b = e.target.closest('button[data-f]'); if (!b) return;
            fitMode = b.dataset.f;
            fitTouched = true;
            applyFitChips();
            // Seed the custom boxes from what is on screen, so switching to
            // «своя» starts from the current length instead of a stranger's 0:30.
            if (fitMode === 'custom' && !customSec) {
                var seed = song ? Math.round(song.duration) : 30;
                customSec = seed;
                var mn = $('mvmCustomMin'), sc = $('mvmCustomSec');
                if (mn) mn.value = String(Math.floor(seed / 60));
                if (sc) sc.value = String(seed % 60);
            }
            syncLoopRow();
            try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'fit', fitMode); } catch (_) {}
            ae('feature_use', { f: 'fit_mode', v: fitMode, song: song ? Math.round(song.duration) : 0, src: Math.round(footageSec()) });
            updateOutputHint();
        });
        function readCustom() {
            var mn = parseInt(($('mvmCustomMin') || {}).value, 10) || 0;
            var sc = parseInt(($('mvmCustomSec') || {}).value, 10) || 0;
            customSec = Math.max(3, Math.min(30 * 60, mn * 60 + sc));
            syncLoopRow();
            updateOutputHint();
        }
        ['mvmCustomMin', 'mvmCustomSec'].forEach(function (id) {
            var el = $(id); if (el) { el.addEventListener('input', readCustom); el.addEventListener('change', readCustom); }
        });

        // The blade prints the 12-clip floor; raise the label to this device's
        // adaptive cap so it always agrees with the «n из max» counter.
        var limEl = $('mvmClipsLimit');
        if (limEl && T.upload_clips_limit) limEl.textContent = t('upload_clips_limit', { max: MAX });
        songEl.addEventListener('tool-uploader:file', function (e) { onUploaderEvent(e, function (fs) { onSong(fs[0]); }); });
        clipsEl.addEventListener('tool-uploader:file', function (e) { onUploaderEvent(e, onClips); });

        $('mvmSongX').addEventListener('click', removeSong);
        $('mvmGo').addEventListener('click', render);
        // NOT `addEventListener('click', download)` — the listener is handed the
        // MouseEvent, which download() would read as its `auto` flag and log
        // every human click as a page-initiated save.
        $('mvmDownload').addEventListener('click', function () { download(false); });
        $('mvmReset').addEventListener('click', resetProject);
        // Cancel guard: a stray tap must not kill a minutes-long render. First
        // tap arms an inline confirm (button turns into «Прервать?»), the second
        // tap within 6 s aborts for real; no tap → quietly reverts.
        var cancelBtn = $('mvmCancel');
        var cancelArmTs = 0, cancelLabel0 = cancelBtn ? cancelBtn.textContent : '';
        function disarmCancel() {
            cancelArmTs = 0;
            if (cancelBtn) { cancelBtn.textContent = cancelLabel0; cancelBtn.classList.remove('mvm-cancel-armed'); }
        }
        if (cancelBtn) cancelBtn.addEventListener('click', function () {
            var now = Date.now();
            if (cancelArmTs && now - cancelArmTs < 6000) {
                disarmCancel();
                cancelRequested = true;
                if (webcodecsAbort) { try { webcodecsAbort.abort(); } catch (_) {} }  // cancel the hardware encode too
                $('mvmStatus').textContent = '…';
                return;
            }
            cancelArmTs = now;
            cancelBtn.textContent = (T.reset_guard_confirm || 'Stop') + '?';
            cancelBtn.classList.add('mvm-cancel-armed');
            ae('feature_use', { f: 'cancel_armed' });
            setTimeout(function () { if (cancelArmTs && Date.now() - cancelArmTs >= 5900) disarmCancel(); }, 6000);
        });

        $('mvmCut').addEventListener('click', function (e) {
            var b = e.target.closest('button[data-v]'); if (!b) return;
            cutDensity = b.dataset.v;
            $('mvmCut').querySelectorAll('button').forEach(function (x) { x.classList.toggle('on', x === b); });
            try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'cut', cutDensity); } catch (_) {}
            updateOutputHint();
        });
        // mode toggle: beat montage vs sequential (clips in full, in order)
        var modeEl = $('mvmMode');
        if (modeEl) modeEl.addEventListener('click', function (e) {
            var b = e.target.closest('button[data-m]'); if (!b) return;
            mode = b.dataset.m;
            modeEl.querySelectorAll('button').forEach(function (x) { x.classList.toggle('on', x === b); });
            var cr = $('mvmCutRow'); if (cr) cr.classList.toggle('mvm-hidden', mode === 'sequential');
            var lr = $('mvmLayoutRow'); if (lr) lr.classList.toggle('mvm-hidden', mode === 'sequential');  // collage cycles on beats
            if (!fitTouched) { fitMode = defaultFitFor(mode); applyFitChips(); }
            syncLoopRow();   // the loop question follows the LENGTH, not the mode
            try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'mode', mode); } catch (_) {}
            ae('feature_use', { f: 'mode_change', v: mode });
            updateOutputHint();
        });
        // layout toggle: single-frame montage vs beat-synced collage (WebCodecs)
        var layoutEl = $('mvmLayout');
        if (layoutEl) layoutEl.addEventListener('click', function (e) {
            var b = e.target.closest('button[data-c]'); if (!b) return;
            collage = b.dataset.c === 'collage';
            layoutEl.querySelectorAll('button').forEach(function (x) { x.classList.toggle('on', x === b); });
            try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'collage', collage ? '1' : '0'); } catch (_) {}
            ae('feature_use', { f: 'layout_change', v: collage ? 'collage' : 'single' });
            // Every other settings control re-plans (mode, cut density, quality,
            // aspect); layout was the one that did not, so the exposed plan went
            // stale the moment collage was switched on.
            updateOutputHint();
            syncTransitionHint();
        });
        var mv = $('mvmMusicVol');
        mv.addEventListener('input', function () { $('mvmMusicVolV').textContent = mv.value + '%'; });

        // The device estimate is per (engine × quality) — a quality change must
        // refresh the hint, or it quotes the previous quality's rate.
        var qEl = $('mvmQuality'); if (qEl) qEl.addEventListener('change', updateOutputHint);
        var aEl = $('mvmAspect'); if (aEl) aEl.addEventListener('change', updateOutputHint);

        // Fade / loudness toggles — checkboxes persisted by hand (the shared
        // bind() targets value-carrying controls; a checkbox round-trips
        // through `checked`). Duck & loop deliberately NOT persisted: they are
        // per-project questions with safe defaults (both ON).
        var fadeEl = $('mvmFade');
        if (fadeEl) {
            try { fadeEl.checked = (window.__toolSettings && window.__toolSettings.load(SLUG, 'fade', '0')) === '1'; } catch (_) {}
            fadeEl.addEventListener('change', function () {
                try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'fade', fadeEl.checked ? '1' : '0'); } catch (_) {}
                ae('feature_use', { f: 'music_fade_toggle', v: fadeEl.checked ? 1 : 0 });
            });
        }
        var normEl = $('mvmNorm');
        if (normEl) {
            try { normEl.checked = (window.__toolSettings && window.__toolSettings.load(SLUG, 'norm', '0')) === '1'; } catch (_) {}
            normEl.addEventListener('change', function () {
                try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'norm', normEl.checked ? '1' : '0'); } catch (_) {}
                ae('feature_use', { f: 'loudnorm_toggle', v: normEl.checked ? 1 : 0 });
            });
        }
        var duckEl = $('mvmDuck');
        if (duckEl) duckEl.addEventListener('change', function () {
            ae('feature_use', { f: 'duck_toggle', v: duckEl.checked ? 1 : 0 });
        });
        var loopEl = $('mvmLoop');
        if (loopEl) loopEl.addEventListener('change', function () {
            ae('feature_use', { f: 'loop_toggle', v: loopEl.checked ? 1 : 0 });
            updateOutputHint();   // «tail will be silent» is a function of this switch
        });

        // #824 class, song edition: the picker opens fine, but the user's music
        // lives in a streaming app (Яндекс Музыка / Apple Music / Spotify) —
        // DRM keeps it out of the file picker, so it looks empty and they cancel
        // repeatedly, then rage-quit. After the 2nd consecutive dismiss, show
        // the honest explainer under the song zone. Reset on a successful pick.
        var songCancels = 0;
        var songInput = document.querySelector('[data-uploader-for=mvmSong]');
        if (songInput && 'oncancel' in songInput) {
            songInput.addEventListener('cancel', function () {
                songCancels++;
                if (songCancels >= 2) {
                    var h = $('mvmSongHint');
                    if (h && h.classList.contains('mvm-hidden')) {
                        h.classList.remove('mvm-hidden');
                        ae('feature_use', { f: 'song_pick_hint_shown' });
                    }
                }
            });
        }
        songEl.addEventListener('tool-uploader:file', function () {
            songCancels = 0;
            var h = $('mvmSongHint'); if (h) h.classList.add('mvm-hidden');
        });

        // restore persisted settings
        if (window.__toolSettings) {
            try {
                window.__toolSettings.bind(SLUG, $('mvmAspect'), 'aspect', '16:9');
                window.__toolSettings.bind(SLUG, $('mvmQuality'), 'quality', 'med');
                window.__toolSettings.bind(SLUG, $('mvmMusicVol'), 'musicvol', '100');
                window.__toolSettings.bind(SLUG, $('mvmEffect'), 'effect', 'none');
                window.__toolSettings.bind(SLUG, $('mvmTransition'), 'transition', 'cut');
                $('mvmMusicVolV').textContent = $('mvmMusicVol').value + '%';
                cutDensity = window.__toolSettings.load(SLUG, 'cut', 'bar') || 'bar';
                $('mvmCut').querySelectorAll('button').forEach(function (x) { x.classList.toggle('on', x.dataset.v === cutDensity); });
                mode = window.__toolSettings.load(SLUG, 'mode', 'beat') || 'beat';
                var _me = $('mvmMode'); if (_me) _me.querySelectorAll('button').forEach(function (x) { x.classList.toggle('on', x.dataset.m === mode); });
                var _cr = $('mvmCutRow'); if (_cr) _cr.classList.toggle('mvm-hidden', mode === 'sequential');
                syncLoopRow();
                collage = (window.__toolSettings.load(SLUG, 'collage', '0') === '1');
                var _le = $('mvmLayout'); if (_le) _le.querySelectorAll('button').forEach(function (x) { x.classList.toggle('on', (x.dataset.c === 'collage') === collage); });
                var _lr = $('mvmLayoutRow'); if (_lr) _lr.classList.toggle('mvm-hidden', mode === 'sequential');
                if (preview && preview.setTransition) preview.setTransition(transitionKind());
                syncTransitionHint();
                /* Contract default is «под песню» on purpose: it is the answer
                   that cannot surprise anyone — the clip ends when the music
                   does. «Под видео» is a deliberate choice, never a leftover. */
                var storedFit = window.__toolSettings.load(SLUG, 'fit', '');
                fitTouched = storedFit === 'song' || storedFit === 'video' || storedFit === 'custom';
                fitMode = fitTouched ? storedFit : defaultFitFor(mode);
                applyFitChips();
            } catch (_) {}
        }

        /* Delivery switches. Auto-save defaults ON: the measured failure is a
           finished clip reaching nobody, and a file quietly landing in Downloads
           is the smaller surprise of the two. It stays a checkbox because the
           output averages 63 MB and someone who only wants to preview should be
           able to say so. */
        if (window.__toolSettings && $('mvmAutoDl')) {
            try { window.__toolSettings.bind(SLUG, $('mvmAutoDl'), 'autodl', true); } catch (_) {}
        }
        var notifyEl = $('mvmNotify');
        if (notifyEl) {
            var notifyRow = notifyEl.closest ? notifyEl.closest('.mvm-check') : null;
            if (typeof Notification === 'undefined') { if (notifyRow) notifyRow.style.display = 'none'; }
            else {
                notifyEl.checked = (Notification.permission === 'granted' &&
                    window.__toolSettings && window.__toolSettings.load(SLUG, 'notify', '0') === '1');
                notifyEl.addEventListener('change', function () {
                    if (!notifyEl.checked) {
                        if (window.__toolSettings) window.__toolSettings.save(SLUG, 'notify', '0');
                        return;
                    }
                    /* Asked from the tick, i.e. inside a real gesture — a page-load
                       prompt is the thing everybody blocks reflexively. A denial
                       is final in the browser, so say so instead of leaving a
                       ticked box that will never fire. */
                    if (Notification.permission === 'granted') {
                        if (window.__toolSettings) window.__toolSettings.save(SLUG, 'notify', '1');
                        ae('feature_use', { f: 'notify_optin', perm: 'granted' });
                        return;
                    }
                    /* Older WebKit implements requestPermission() with a
                       CALLBACK and returns undefined — `.then` on that throws
                       inside a change handler, which would leave the tick on
                       and no permission behind it. Normalise both shapes. */
                    // `settled` must exist BEFORE the call: the callback form can
                    // answer synchronously, and a `var` initialised afterwards
                    // would reset the guard and let the promise answer a second
                    // time (two notify_optin events for one tick).
                    var settled = false;
                    var ask = Notification.requestPermission(function (perm) { settle(perm); });
                    function settle(perm) {
                        if (settled) return;
                        settled = true;
                        onPerm(perm);
                    }
                    if (ask && typeof ask.then === 'function') ask.then(settle, function () { notifyEl.checked = false; });
                    function onPerm(perm) {
                        ae('feature_use', { f: 'notify_optin', perm: perm });
                        if (perm === 'granted') {
                            if (window.__toolSettings) window.__toolSettings.save(SLUG, 'notify', '1');
                        } else {
                            notifyEl.checked = false;
                            if (window.__toast) window.__toast(t('notify_blocked'), 'warning');
                        }
                    }
                });
            }
        }

        offerRestore();
        offerSavedResult();
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
})();
